import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Timeline from "../components/Timeline";
import PermitPdf from "../components/PermitPdf";
import { usePopup } from "../components/PopupProvider";
import { formatPermitTrackingId } from "../utils/permitTracking";
import {
  ptw_can_create_permit,
  ptw_can_delete_permit,
  ptw_can_review_competent_person,
  ptw_can_review_clearance_department,
  ptw_can_approve_hot_work,
  ptw_can_approve_cold_work,
  ptw_can_approve_electrical_work,
  ptw_can_approve_confined_space_entry,
  ptw_can_approve_work_at_height,
  ptw_can_approve_excavation_work,
  ptw_can_approve_chemical_handling,
  ptw_can_approve_by_safety,
  ptw_can_issue_permit,
  ptw_can_record_gas_test,
  ptw_can_manage_isolation,
  ptw_can_manage_revalidation,
  ptw_can_manage_meeting,
  ptw_can_manage_tbt,
  ptw_can_activate_permit,
  headers,
  restApiUrl,
  multipartHeaders,
} from "./app_props";

// Label/value pair: small uppercase muted label above a full-weight value. See index.css `#permit-detail .pd-field`.
const Field = ({ label, value, className = "" }) => (
  <div className={`pd-field ${className}`}>
    <div className="pd-field-label">{label}</div>
    <div className="pd-field-value">{value}</div>
  </div>
);

const PermitDetail = () => {
  const { showConfirm, showError, showWarning, showInfo, showSuccess } =
    usePopup();
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const sites = JSON.parse(user.sites);
  const { id } = useParams();
  const [permit, setPermit] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditHasMore, setAuditHasMore] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const auditContainerRef = useRef(null);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkAction, setRemarkAction] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [remarkDate, setRemarkDate] = useState("");
  const [checklistItems, setChecklistItems] = useState([]);
  const [cycleFilter, setCycleFilter] = useState("all");
  const [returnComment, setReturnComment] = useState("");
  const [permitTypeChecklistMap, setPermitTypeChecklistMap] = useState({});
  const [allChecklists, setAllChecklists] = useState([]);
  const [permitTypeBlocks, setPermitTypeBlocks] = useState([]);
  const [riskItems, setRiskItems] = useState([]);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [clearanceEdits, setClearanceEdits] = useState({});
  const [clearanceServerApproved, setClearanceServerApproved] = useState({});
  const [clearanceRemarks, setClearanceRemarks] = useState({});
  const [clearanceValidationErrors, setClearanceValidationErrors] = useState(
    {},
  );
  const [hodEditApproved, setHodEditApproved] = useState(null);
  const [hodEditRemark, setHodEditRemark] = useState("");
  const [hodValidationError, setHodValidationError] = useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  // Shift Handover state
  const [shiftHandovers, setShiftHandovers] = useState([]);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [handoverIncomingUserId, setHandoverIncomingUserId] = useState("");
  const [handoverRemarks, setHandoverRemarks] = useState("");
  const [respondingHandover, setRespondingHandover] = useState(null); // { id, accepted }
  const [handoverRejectRemark, setHandoverRejectRemark] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);

  // Tool Box Talk state
  const [toolBoxTalks, setToolBoxTalks] = useState([]);
  const [showTbtModal, setShowTbtModal] = useState(false);
  const [tbtTopic, setTbtTopic] = useState("");
  const [tbtNotes, setTbtNotes] = useState("");

  const [showResumptionModal, setShowResumptionModal] = useState(false);
  const [resumptionRemark, setResumptionRemark] = useState("");
  const [reloadCounter, setReloadCounter] = useState(0);

  const [gasTests, setGasTests] = useState([]);
  const [showGasTestModal, setShowGasTestModal] = useState(false);
  const [gasTestParamOptions, setGasTestParamOptions] = useState([]);
  const [gasTestEntries, setGasTestEntries] = useState([{ parameter: "", reading: "" }]);
  const [gasTestCategory, setGasTestCategory] = useState("PRE_PERMIT");
  const [gasTestValidUntil, setGasTestValidUntil] = useState("");
  const [gasTestTesterId, setGasTestTesterId] = useState("");
  const [tbtAttendeeList, setTbtAttendeeList] = useState([]);
  const [tbtSelectedEmployeeId, setTbtSelectedEmployeeId] = useState("");
  const [tbtContractPersonName, setTbtContractPersonName] = useState("");
  const [tbtContractPersonAgency, setTbtContractPersonAgency] = useState("");
  const [simopsConflicts, setSimopsConflicts] = useState([]);
  const [showBoxUpModal, setShowBoxUpModal] = useState(false);
  const [boxUpRemark, setBoxUpRemark] = useState("");

  // Coordination meetings state
  const [coordinationMeetings, setCoordinationMeetings] = useState([]);

  // Master/Child hierarchy state
  const [linkChildId, setLinkChildId] = useState("");
  const [showLinkForm, setShowLinkForm] = useState(false);

  // Blanket permit check-ins
  const [blanketCheckins, setBlanketCheckins] = useState([]);
  const [blanketCheckinRemark, setBlanketCheckinRemark] = useState("");
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingLocation, setMeetingLocation] = useState("");
  const [meetingAgenda, setMeetingAgenda] = useState("");
  const [meetingMinutes, setMeetingMinutes] = useState("");
  const [meetingAttendeeList, setMeetingAttendeeList] = useState([]);
  const [meetingSelectedEmployeeId, setMeetingSelectedEmployeeId] = useState("");
  const [meetingContractPersonName, setMeetingContractPersonName] = useState("");
  const [meetingContractPersonAgency, setMeetingContractPersonAgency] = useState("");

  // Revalidation state
  const [revalidations, setRevalidations] = useState([]);
  const [showRevalModal, setShowRevalModal] = useState(false);
  const [revalShiftLabel, setRevalShiftLabel] = useState("Morning");
  const [revalValidUntil, setRevalValidUntil] = useState("");
  const [revalIssuerRemark, setRevalIssuerRemark] = useState("");
  const [showReceiverSignModal, setShowReceiverSignModal] = useState(false);

  // Returns the Date the next shift revalidation is due, or null if not applicable.
  const getNextRevalidationDue = () => {
    if (!permit || !permit.revalidationRequired || !permit.revalidationIntervalHours) return null;
    const latest = [...revalidations].sort(
      (a, b) => new Date(b.issuerSignedAt || b.revalidationDate || 0) - new Date(a.issuerSignedAt || a.revalidationDate || 0),
    )[0];
    const baseline = latest?.validUntil || latest?.issuerSignedAt || permit.actualStart || permit.scheduleStart;
    if (!baseline) return null;
    return new Date(new Date(baseline).getTime() + permit.revalidationIntervalHours * 60 * 60 * 1000);
  };

  const toDatetimeLocalInput = (date) => {
    if (!date) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };
  const [receiverSignId, setReceiverSignId] = useState(null);
  const [receiverSignRemark, setReceiverSignRemark] = useState("");

  // Isolation Records state
  const [isolationRecords, setIsolationRecords] = useState([]);
  const [showIsoModal, setShowIsoModal] = useState(false);
  const [isoPoint, setIsoPoint] = useState("");
  const [isoMethod, setIsoMethod] = useState("LOCKOUT");
  const [isoEquipTag, setIsoEquipTag] = useState("");
  const [isoLockTag, setIsoLockTag] = useState("");
  const [showReinstateModal, setShowReinstateModal] = useState(false);
  const [reinstateId, setReinstateId] = useState(null);
  const [reinstateRemark, setReinstateRemark] = useState("");

  // JSA state
  const [jsaList, setJsaList] = useState([]);
  const [showJsaModal, setShowJsaModal] = useState(false);
  const [jsaTitle, setJsaTitle] = useState("");
  const [jsaWorkDesc, setJsaWorkDesc] = useState("");
  const [jsaSteps, setJsaSteps] = useState([{ taskDescription: "", potentialHazards: "", controlMeasures: "", responsiblePerson: "", residualRisk: "" }]);
  const [showJsaApproveModal, setShowJsaApproveModal] = useState(false);
  const [jsaApproveId, setJsaApproveId] = useState(null);
  const [jsaApproveRemark, setJsaApproveRemark] = useState("");

  const setButtonsDisabledWithLog = (val) => {
    setButtonsDisabled(val);
  };

  const extractDepartmentMap = (sites) => {
    return new Map(
      sites.map((site) => [
        site.name,
        site.departmentList.map((department) => ({
          departmentName: department.name,
          departmentHOD: department.departmentHOD.username,
          departmentHODEmail: department.departmentHOD.emailAddress,
          id: department.id,
        })),
      ])
    );
  };

  const departmentMap = extractDepartmentMap(sites);

  const hasIssuerDepartmentHOD =
    permit && permit.issuerDepartmentHOD
      ? user.username === permit?.issuerDepartmentHOD.username
      : undefined;

  useEffect(() => {
    if (permit) {
      setHodEditApproved(permit.hodApproved === undefined ? null : permit.hodApproved);
      setHodEditRemark(permit.hodApprovalRemark || "");
    }
  }, [permit]);
  const [editedBlockIds, setEditedBlockIds] = useState([]);
  const [checklistReview, setChecklistReview] = useState({});
  const [userDeptAccess, setUserDeptAccess] = useState([]);
  const groupedClearances = useMemo(() => {
    const map = {};
    if (permit && Array.isArray(permit.clearanceDepartments)) {
      // Group clearance entries by cycle (cycleNo) and approvalType
      permit.clearanceDepartments.forEach((c) => {
        const cycle = c && c.cycleNo != null ? c.cycleNo : 0;
        const key = `cycle-${cycle}`;
        if (!map[key]) {
          map[key] = {
            cycleNo: cycle,
            approvalType: (c && c.approvalType) || "INITIAL",
            items: [],
          };
        }
        map[key].items.push(c);
      });
    }
    return map;
  }, [permit]);
  const navigate = useNavigate();

  useEffect(() => {
    // Load users who have access to this permit's site (via UserSite) for the incoming
    // shift-handover selector, so only users assigned to the permit's site are offered.
    if (!permit?.siteId) return;
    fetch(`${restApiUrl}/user/by-site/${permit.siteId}`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { setAllUsers(Array.isArray(j) ? j : []); })
      .catch(() => { });
  }, [permit?.siteId]);

  useEffect(() => {
    Promise.all([
      fetch(`${restApiUrl}/api/checklists?page=0&size=500&sort=name,asc`, {
        method: "GET",
        mode: "cors",
        headers: headers(),
      }).then((res) => res.json()),
      fetch(`${restApiUrl}/api/permit-types?page=0&size=500&sort=name,asc`, {
        method: "GET",
        mode: "cors",
        headers: headers(),
      }).then((res) => res.json()),
    ])
      .then(([checklistsResp, permitTypesResp]) => {
        const checklistList =
          (checklistsResp &&
            checklistsResp.data &&
            checklistsResp.data.content) ||
          checklistsResp.data ||
          [];
        const permitTypes =
          (permitTypesResp &&
            permitTypesResp.data &&
            permitTypesResp.data.content) ||
          permitTypesResp.data ||
          [];

        setAllChecklists(checklistList || []);

        const checklistById = {};
        (checklistList || []).forEach((item) => {
          if (item && item.id != null) {
            checklistById[String(item.id)] = item;
          }
        });

        const nextMap = {};
        (permitTypes || []).forEach((pt) => {
          const mapped = (pt.checklistIds || [])
            .map((checklistId) => checklistById[String(checklistId)])
            .filter(Boolean);
          nextMap[String(pt.id)] = mapped;
        });
        setPermitTypeChecklistMap(nextMap);
      })
      .catch((err) => console.error("Failed to load permit type checklists", err));
  }, []);

  useEffect(() => {
    // load full permit (permit + checklist + risk + attachments)
    fetch(`${restApiUrl}/api/permits/${id}/full`, {
      method: "GET",
      mode: "cors",
      headers: headers(),
    })
      .then((res) => res.json())
      .then((j) => {
        if (j && j.success && j.data) {
          const full = j.data;
          if (full.permit) {
            setPermit(full.permit);
            if (Array.isArray(full.permit.clearanceDepartments)) {
              const map = {};
              const serverMap = {};
              const remarksMap = {};
              full.permit.clearanceDepartments.forEach((c) => {
                if (c && c.id) {
                  map[c.id] = c.approved == null ? null : !!c.approved; // initial UI edit state (null = not yet reviewed)
                  serverMap[c.id] = !!c.approved; // backend-sent approval
                  if (c.remark) remarksMap[c.id] = c.remark;
                }
              });
              setClearanceEdits(map);
              setClearanceServerApproved(serverMap);
              setClearanceRemarks(remarksMap);
            }
          }
          if (full.checklistItems) setChecklistItems(full.checklistItems);
          // Prefer the permit-scoped blocks if present (some APIs return
          // blocks inside `full.permit.permitTypeBlocks`), otherwise fall
          // back to top-level `full.permitTypeBlocks`.
          const blocksSource =
            full.permit && Array.isArray(full.permit.permitTypeBlocks)
              ? full.permit.permitTypeBlocks
              : full.permitTypeBlocks;
          if (blocksSource) {
            // If checklist items are returned at top-level, attach them to the
            // corresponding permit-type blocks here so the UI can display
            // block-specific checklists regardless of where the API placed them.
            if (full.checklistItems && Array.isArray(full.checklistItems)) {
              const byPbt = {};
              full.checklistItems.forEach((ci) => {
                const key1 =
                  ci.permitPermitTypeId ||
                  (ci.permitPermitType && ci.permitPermitType.id) ||
                  null;
                const key2 = ci.permitTypeId || null; // accept permitTypeId mapping
                if (key1 != null) {
                  if (!byPbt[key1]) byPbt[key1] = [];
                  byPbt[key1].push(ci);
                }
                if (key2 != null) {
                  if (!byPbt[key2]) byPbt[key2] = [];
                  byPbt[key2].push(ci);
                }
              });

              // Build a lookup of any embedded checklist/master objects found
              // anywhere in the full response (prefer embedded `checklist` objects
              // or entries that contain name/text for a given checklist id).
              const masterById = {};
              const scanList = [
                ...(full.checklistItems || []),
                ...(full.permit && full.permit.checklistItems
                  ? full.permit.checklistItems
                  : []),
              ];
              scanList.forEach((ci) => {
                if (ci && ci.checklist && ci.checklist.id) {
                  masterById[String(ci.checklist.id)] = ci.checklist;
                }
                if (
                  ci &&
                  (ci.permitTypeChecklistId ||
                    ci.permitTypeChecklistId === 0) &&
                  ci.text
                ) {
                  masterById[String(ci.permitTypeChecklistId)] = masterById[
                    String(ci.permitTypeChecklistId)
                  ] || { checklist: ci.text, name: ci.text };
                }
              });

              const blocksWithChecklist = (blocksSource || []).map((b) => ({
                ...b,
                checklistItems:
                  byPbt[b.id] ||
                  byPbt[b.permitTypeId] ||
                  b.checklistItems ||
                  [],
              }));

              const enriched =
                masterById && Object.keys(masterById).length > 0
                  ? blocksWithChecklist.map((b) => ({
                    ...b,
                    checklistItems: (b.checklistItems || []).map((ci) => {
                      if (
                        ci &&
                        !ci.text &&
                        (ci.permitTypeChecklistId ||
                          ci.permitTypeChecklistId === 0)
                      ) {
                        const found =
                          masterById[String(ci.permitTypeChecklistId)];
                        if (found) {
                          return {
                            ...ci,
                            text:
                              ci.text ||
                              found.name ||
                              found.checklist ||
                              found.checklist,
                            checklist: ci.checklist || found,
                            fields:
                              ci.fields && ci.fields.length > 0
                                ? ci.fields
                                : found.fields || [],
                            fieldsValues: ci.fieldsValues || {},
                          };
                        }
                        // fallback label when master not found yet; we'll try to fetch below
                        return { ...ci, _needsMaster: true };
                      }
                      return ci;
                    }),
                  }))
                  : blocksWithChecklist.map((b) => ({
                    ...b,
                    checklistItems: (b.checklistItems || []).map((ci) => ({
                      ...ci,
                    })),
                  }));

              // If some items still need master text, fetch master checklists
              const needs = new Set();
              enriched.forEach((b) => {
                (b.checklistItems || []).forEach((ci) => {
                  if (
                    ci &&
                    ci._needsMaster &&
                    (ci.permitTypeChecklistId || ci.permitTypeChecklistId === 0)
                  ) {
                    needs.add(String(ci.permitTypeChecklistId));
                  }
                });
              });

              if (needs.size > 0) {
                fetch(
                  `${restApiUrl}/api/checklists?page=0&size=200&sort=name,asc`,
                  {
                    method: "GET",
                    mode: "cors",
                    headers: headers(),
                  },
                )
                  .then((r) => r.json())
                  .then((cj) => {
                    const all =
                      (cj && cj.data && cj.data.content) || cj.data || cj || [];
                    const ckMap = {};
                    (all || []).forEach((c) => {
                      if (c && c.id) ckMap[String(c.id)] = c;
                    });
                    const final = enriched.map((b) => ({
                      ...b,
                      checklistItems: (b.checklistItems || []).map((ci) => {
                        if (ci && ci._needsMaster) {
                          const found = ckMap[String(ci.permitTypeChecklistId)];
                          if (found) {
                            return {
                              ...ci,
                              text:
                                ci.text ||
                                found.name ||
                                found.checklist ||
                                found.text ||
                                `Checklist ${ci.permitTypeChecklistId}`,
                              checklist: ci.checklist || found,
                              fields:
                                ci.fields && ci.fields.length > 0
                                  ? ci.fields
                                  : found.fields || [],
                              fieldsValues: ci.fieldsValues || {},
                            };
                          }
                          return {
                            ...ci,
                            text:
                              ci.text ||
                              `Checklist ${ci.permitTypeChecklistId}`,
                          };
                        }
                        return ci;
                      }),
                    }));
                    setPermitTypeBlocks(final);
                  })
                  .catch(() => {
                    // On error, fall back to simple labels
                    const final = enriched.map((b) => ({
                      ...b,
                      checklistItems: (b.checklistItems || []).map((ci) => ({
                        ...ci,
                        text:
                          ci.text ||
                          (ci.permitTypeChecklistId
                            ? `Checklist ${ci.permitTypeChecklistId}`
                            : ci.text),
                      })),
                    }));
                    setPermitTypeBlocks(final);
                  });
              } else {
                // no additional masters needed
                setPermitTypeBlocks(
                  enriched.map((b) => ({
                    ...b,
                    checklistItems: (b.checklistItems || []).map((ci) => ({
                      constText: undefined,
                      ...ci,
                    })),
                  })),
                );
              }
            } else {
              setPermitTypeBlocks(blocksSource);
            }
            // Risk assessment items (if returned by full endpoint)
            if (
              full.riskAssessmentItems &&
              Array.isArray(full.riskAssessmentItems)
            ) {
              setRiskItems(full.riskAssessmentItems);
            }
            if (full.riskAssessment) {
              setRiskAssessment(full.riskAssessment);
            }
          }
        }
      })
      .catch((err) => console.error("Failed to load full permit", err));

    loadAuditLogs(0, false);

    // Load revalidations for this permit
    fetch(`${restApiUrl}/api/permit-revalidations/permit/${id}`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { if (j && j.success) setRevalidations(j.data || []); })
      .catch(() => { });

    // Load isolation records for this permit
    fetch(`${restApiUrl}/api/isolation-records/permit/${id}`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { if (j && j.success) setIsolationRecords(j.data || []); })
      .catch(() => { });

    // Load JSAs for this permit
    fetch(`${restApiUrl}/api/jsa/permit/${id}`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { if (j && j.success) setJsaList(j.data || []); })
      .catch(() => { });

    // SIMOPS conflict check
    fetch(`${restApiUrl}/api/permits/${id}/simops`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { setSimopsConflicts(Array.isArray(j) ? j : []); })
      .catch(() => { });

    // Blanket permit check-ins (loaded regardless; service returns empty for non-blanket)
    fetch(`${restApiUrl}/api/permits/${id}/blanket-checkins`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { setBlanketCheckins(Array.isArray(j) ? j : []); })
      .catch(() => { });

    // Coordination meetings
    fetch(`${restApiUrl}/api/permits/${id}/coordination-meetings`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { setCoordinationMeetings(Array.isArray(j) ? j : []); })
      .catch(() => { });

    // Load gas tests for this permit
    fetch(`${restApiUrl}/api/gas-tests/permit/${id}`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { if (j && j.success) setGasTests(j.data || []); })
      .catch(() => { });

    // Load Tool Box Talks for this permit
    fetch(`${restApiUrl}/api/tool-box-talks/permit/${id}`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { if (j && j.success) setToolBoxTalks(j.data || []); })
      .catch(() => { });

    // Load shift handovers for this permit
    fetch(`${restApiUrl}/api/shift-handovers/permit/${id}`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { if (j && j.success) setShiftHandovers(j.data || []); })
      .catch(() => { });

    // Load all employees for the coordination meeting attendee selector
    fetch(`${restApiUrl}/employees`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => { setAllEmployees(Array.isArray(j) ? j : []); })
      .catch(() => { });

    // Load gas test parameter master data
    fetch(`${restApiUrl}/api/gas-test-parameters?size=200`, {
      method: "GET", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => {
        const list = j?.data?.content || [];
        setGasTestParamOptions(list);
      })
      .catch(() => { });
  }, [id, reloadCounter]);

  const addTbtEmployeeAttendee = () => {
    if (!tbtSelectedEmployeeId) return;
    const emp = allEmployees.find((e) => String(e.id) === String(tbtSelectedEmployeeId));
    if (!emp) return;
    const name = emp.empCode ? `${emp.empCode} - ${emp.employeeName}` : emp.employeeName;
    setTbtAttendeeList((prev) => {
      if (prev.some((a) => a.employeeId === emp.id)) return prev;
      return [...prev, { employeeId: emp.id, label: name }];
    });
    setTbtSelectedEmployeeId("");
  };

  const addTbtContractPersonAttendee = () => {
    const name = tbtContractPersonName.trim();
    if (!name) { showWarning("Enter the contract person's name first."); return; }
    const agency = tbtContractPersonAgency.trim();
    setTbtAttendeeList((prev) => {
      if (prev.some((a) => a.contractorName === name && a.contractorAgency === agency)) return prev;
      const label = agency ? `${name} (${agency})` : name;
      return [...prev, { contractorName: name, contractorAgency: agency || null, label }];
    });
    setTbtContractPersonName("");
    setTbtContractPersonAgency("");
  };

  const removeTbtAttendee = (label) => {
    setTbtAttendeeList((prev) => prev.filter((a) => a.label !== label));
  };

  const submitTbt = () => {
    if (!tbtTopic.trim()) { showWarning("Please enter a topic for the Tool Box Talk."); return; }
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/tool-box-talks`, {
      method: "POST", mode: "cors", headers: headers(),
      body: JSON.stringify({
        permitId: Number(id),
        topic: tbtTopic,
        notes: tbtNotes,
        attendees: tbtAttendeeList.map((a) => ({
          employeeId: a.employeeId || null,
          contractorName: a.contractorName || null,
          contractorAgency: a.contractorAgency || null,
        })),
        conductedAt: new Date().toISOString().slice(0, 19),
      }),
    })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success) {
          showSuccess("Tool Box Talk recorded successfully.");
          setToolBoxTalks((prev) => [j.data, ...prev]);
          setShowTbtModal(false);
          setTbtTopic(""); setTbtNotes(""); setTbtAttendeeList([]);
          setTbtSelectedEmployeeId(""); setTbtContractPersonName(""); setTbtContractPersonAgency("");
        } else {
          showError(j.message || "Failed to record Tool Box Talk.");
        }
      })
      .catch(() => showError("Network error while recording TBT."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitRevalidation = () => {
    setButtonsDisabledWithLog(true);
    const body = {
      permitId: Number(id),
      shiftLabel: revalShiftLabel,
      revalidationDate: new Date().toISOString().slice(0, 10),
      issuerRemark: revalIssuerRemark.trim(),
      validUntil: revalValidUntil ? (revalValidUntil.slice(0, 16) + ":00") : null,
    };
    fetch(`${restApiUrl}/api/permit-revalidations`, {
      method: "POST", mode: "cors", headers: headers(), body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(j => {
        if (j && j.success) {
          showSuccess("Revalidation shift recorded.");
          setRevalidations(prev => [...prev, j.data].sort((a, b) => a.shiftNo - b.shiftNo));
          setShowRevalModal(false); setRevalIssuerRemark(""); setRevalValidUntil("");
        } else { showError(j.message || "Failed to record revalidation."); }
      })
      .catch(() => showError("Network error."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitReceiverSign = () => {
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/permit-revalidations/${receiverSignId}/receiver-sign?remark=${encodeURIComponent(receiverSignRemark)}`, {
      method: "POST", mode: "cors", headers: headers(),
    })
      .then(r => r.json())
      .then(j => {
        if (j && j.success) {
          showSuccess("Receiver sign-off recorded.");
          setRevalidations(prev => prev.map(x => x.id === receiverSignId ? j.data : x));
          setShowReceiverSignModal(false); setReceiverSignRemark("");
        } else { showError(j.message || "Failed to record receiver sign-off."); }
      })
      .catch(() => showError("Network error."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitIsolation = () => {
    if (!isoPoint.trim()) { showWarning("Please enter the isolation point."); return; }
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/isolation-records`, {
      method: "POST", mode: "cors", headers: headers(),
      body: JSON.stringify({ permitId: Number(id), isolationPoint: isoPoint.trim(), isolationMethod: isoMethod, equipmentTag: isoEquipTag.trim(), lockTagNumber: isoLockTag.trim() }),
    })
      .then(r => r.json())
      .then(j => {
        if (j && j.success) {
          showSuccess("Isolation record added.");
          setIsolationRecords(prev => [j.data, ...prev]);
          setShowIsoModal(false); setIsoPoint(""); setIsoEquipTag(""); setIsoLockTag("");
        } else { showError(j.message || "Failed to add isolation record."); }
      })
      .catch(() => showError("Network error."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitReinstate = () => {
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/isolation-records/${reinstateId}/reinstate?remark=${encodeURIComponent(reinstateRemark)}`, {
      method: "POST", mode: "cors", headers: headers(),
    })
      .then(r => r.json())
      .then(j => {
        if (j && j.success) {
          showSuccess("Isolation reinstated.");
          setIsolationRecords(prev => prev.map(x => x.id === reinstateId ? j.data : x));
          setShowReinstateModal(false); setReinstateRemark("");
        } else { showError(j.message || "Failed to reinstate."); }
      })
      .catch(() => showError("Network error."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitJsa = () => {
    if (!jsaTitle.trim()) { showWarning("Please enter a JSA title."); return; }
    const validSteps = jsaSteps.filter(s => s.taskDescription.trim());
    if (validSteps.length === 0) { showWarning("At least one task step with a description is required."); return; }
    setButtonsDisabledWithLog(true);
    const body = {
      permitId: Number(id),
      title: jsaTitle.trim(),
      workDescription: jsaWorkDesc.trim(),
      taskSteps: validSteps.map((s, i) => ({ ...s, stepNo: i + 1 })),
    };
    fetch(`${restApiUrl}/api/jsa`, {
      method: "POST", mode: "cors", headers: headers(), body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(j => {
        if (j && j.success) {
          showSuccess("JSA created successfully.");
          setJsaList(prev => [j.data, ...prev]);
          setShowJsaModal(false);
          setJsaTitle(""); setJsaWorkDesc("");
          setJsaSteps([{ taskDescription: "", potentialHazards: "", controlMeasures: "", responsiblePerson: "", residualRisk: "" }]);
        } else { showError(j.message || "Failed to create JSA."); }
      })
      .catch(() => showError("Network error creating JSA."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitJsaForApproval = (jsaId) => {
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/jsa/${jsaId}/submit`, { method: "POST", mode: "cors", headers: headers() })
      .then(r => r.json())
      .then(j => {
        if (j && j.success) {
          showSuccess("JSA submitted for approval.");
          setJsaList(prev => prev.map(x => x.id === jsaId ? j.data : x));
        } else { showError(j.message || "Failed to submit JSA."); }
      })
      .catch(() => showError("Network error."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const approveJsa = () => {
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/jsa/${jsaApproveId}/approve?remark=${encodeURIComponent(jsaApproveRemark)}`, {
      method: "POST", mode: "cors", headers: headers(),
    })
      .then(r => r.json())
      .then(j => {
        if (j && j.success) {
          showSuccess("JSA approved.");
          setJsaList(prev => prev.map(x => x.id === jsaApproveId ? j.data : x));
          setShowJsaApproveModal(false); setJsaApproveRemark("");
        } else { showError(j.message || "Failed to approve JSA."); }
      })
      .catch(() => showError("Network error."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitResumption = () => {
    if (!resumptionRemark.trim()) { showWarning("A resumption sign-off remark is required."); return; }
    setButtonsDisabledWithLog(true);
    const url = `${restApiUrl}/api/permits/${id}/activate?remark=${encodeURIComponent(resumptionRemark.trim())}`;
    fetch(url, { method: "POST", mode: "cors", headers: headers() })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success) {
          showSuccess("Permit re-activated with sign-off recorded.");
          setShowResumptionModal(false);
          setResumptionRemark("");
          setReloadCounter((c) => c + 1);
        } else {
          showError(j.message || "Failed to re-activate permit.");
        }
      })
      .catch(() => showError("Network error while re-activating permit."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const addGasTestEntry = () => {
    setGasTestEntries((prev) => {
      const used = new Set(prev.map((e) => e.parameter).filter(Boolean));
      const next = gasTestParamOptions.find((p) => !used.has(p.name));
      return [...prev, { parameter: next?.name || "", reading: "" }];
    });
  };

  const removeGasTestEntry = (idx) => {
    setGasTestEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateGasTestEntry = (idx, field, value) => {
    setGasTestEntries((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const submitGasTest = async () => {
    const entries = gasTestEntries.filter((en) => en.parameter && en.reading.trim());
    if (entries.length === 0) { showWarning("Please enter at least one parameter reading."); return; }
    if (entries.length !== gasTestEntries.length) { showWarning("Please complete or remove any blank parameter rows."); return; }
    if (!gasTestTesterId) { showWarning("Please select a tester."); return; }
    setButtonsDisabledWithLog(true);
    const payload = {
      permitId: Number(id),
      readings: entries.map((en) => ({ parameter: en.parameter, reading: en.reading.trim() })),
      category: gasTestCategory,
      testedAt: new Date().toISOString(),
      validUntil: gasTestValidUntil ? new Date(gasTestValidUntil).toISOString() : null,
      tester: { id: Number(gasTestTesterId) },
    };
    try {
      const r = await fetch(`${restApiUrl}/api/gas-tests`, { method: "POST", mode: "cors", headers: headers(), body: JSON.stringify(payload) });
      const j = await r.json();
      if (j && j.success) {
        setGasTests((prev) => [j.data, ...prev]);
        showSuccess("Gas test recorded.");
        setShowGasTestModal(false);
        setGasTestEntries([{ parameter: gasTestParamOptions[0]?.name || "", reading: "" }]);
        setGasTestCategory("PRE_PERMIT"); setGasTestValidUntil(""); setGasTestTesterId("");
      } else {
        showError(j?.message || "Failed to save gas test.");
      }
    } catch {
      showError("Network error.");
    } finally {
      setButtonsDisabledWithLog(false);
    }
  };

  const submitBlanketCheckin = () => {
    setButtonsDisabledWithLog(true);
    const url = `${restApiUrl}/api/permits/${id}/blanket-checkins` +
      (blanketCheckinRemark.trim() ? `?remarks=${encodeURIComponent(blanketCheckinRemark.trim())}` : "");
    fetch(url, { method: "POST", mode: "cors", headers: headers() })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.id) {
          showSuccess("Daily check-in confirmed.");
          setBlanketCheckinRemark("");
          setBlanketCheckins((prev) => [j, ...prev]);
        } else {
          showError(j.message || "Failed to record check-in.");
        }
      })
      .catch(() => showError("Network error."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitLinkChild = () => {
    const childId = parseInt(linkChildId, 10);
    if (!childId) { showWarning("Enter a valid permit ID."); return; }
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/permits/${id}/children/${childId}`, {
      method: "POST", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.id) {
          showSuccess(`Permit #${childId} linked as child permit.`);
          setShowLinkForm(false); setLinkChildId("");
          setReloadCounter((c) => c + 1);
        } else {
          showError(j.message || "Failed to link child permit.");
        }
      })
      .catch(() => showError("Network error."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const unlinkChild = (childId) => {
    if (!window.confirm(`Remove permit #${childId} as a child of this permit?`)) return;
    fetch(`${restApiUrl}/api/permits/${id}/children/${childId}`, {
      method: "DELETE", mode: "cors", headers: headers(),
    })
      .then(() => { showSuccess("Child permit unlinked."); setReloadCounter((c) => c + 1); })
      .catch(() => showError("Failed to unlink child permit."));
  };

  const addMeetingEmployeeAttendee = () => {
    if (!meetingSelectedEmployeeId) return;
    const emp = allEmployees.find((e) => String(e.id) === String(meetingSelectedEmployeeId));
    if (!emp) return;
    const name = emp.empCode ? `${emp.empCode} - ${emp.employeeName}` : emp.employeeName;
    setMeetingAttendeeList((prev) => {
      if (prev.some((a) => a.employeeId === emp.id)) return prev;
      return [...prev, { employeeId: emp.id, label: name }];
    });
    setMeetingSelectedEmployeeId("");
  };

  const addMeetingContractPersonAttendee = () => {
    const name = meetingContractPersonName.trim();
    if (!name) { showWarning("Enter the contract person's name first."); return; }
    const agency = meetingContractPersonAgency.trim();
    setMeetingAttendeeList((prev) => {
      if (prev.some((a) => a.contractorName === name && a.contractorAgency === agency)) return prev;
      const label = agency ? `${name} (${agency})` : name;
      return [...prev, { contractorName: name, contractorAgency: agency || null, label }];
    });
    setMeetingContractPersonName("");
    setMeetingContractPersonAgency("");
  };

  const removeMeetingAttendee = (label) => {
    setMeetingAttendeeList((prev) => prev.filter((a) => a.label !== label));
  };

  const submitCoordinationMeeting = () => {
    if (!meetingDate) { showWarning("Meeting date is required."); return; }
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/permits/${id}/coordination-meetings`, {
      method: "POST", mode: "cors",
      headers: headers(),
      body: JSON.stringify({
        meetingDate: meetingDate,
        location: meetingLocation,
        agenda: meetingAgenda,
        attendees: meetingAttendeeList.map((a) => ({
          employeeId: a.employeeId || null,
          contractorName: a.contractorName || null,
          contractorAgency: a.contractorAgency || null,
        })),
        minutes: meetingMinutes,
      }),
    })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.id) {
          showSuccess("Coordination meeting recorded.");
          setShowMeetingForm(false);
          setMeetingDate(""); setMeetingLocation(""); setMeetingAgenda("");
          setMeetingMinutes("");
          setMeetingAttendeeList([]); setMeetingSelectedEmployeeId(""); setMeetingContractPersonName(""); setMeetingContractPersonAgency("");
          setCoordinationMeetings((prev) => [j, ...prev]);
        } else {
          showError(j.message || "Failed to record meeting.");
        }
      })
      .catch(() => showError("Network error while saving meeting."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const deleteCoordinationMeeting = (meetingId) => {
    if (!window.confirm("Remove this coordination meeting record?")) return;
    fetch(`${restApiUrl}/api/permits/coordination-meetings/${meetingId}`, {
      method: "DELETE", mode: "cors", headers: headers(),
    })
      .then(() => setCoordinationMeetings((prev) => prev.filter((m) => m.id !== meetingId)))
      .catch(() => showError("Failed to remove meeting."));
  };

  const submitBoxUpClearance = () => {
    if (!boxUpRemark.trim()) { showWarning("A box-up clearance remark is required."); return; }
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/permits/${id}/box-up-clearance?remark=${encodeURIComponent(boxUpRemark.trim())}`, {
      method: "POST", mode: "cors", headers: headers(),
    })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success) {
          showSuccess("Box-up clearance recorded. Confined space is cleared for closure.");
          setShowBoxUpModal(false);
          setBoxUpRemark("");
          setReloadCounter((c) => c + 1);
        } else {
          showError(j.message || "Failed to record box-up clearance.");
        }
      })
      .catch(() => showError("Network error while recording box-up clearance."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitHandover = () => {
    if (!handoverIncomingUserId) { showWarning("Please select the incoming shift-in-charge."); return; }
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}/api/shift-handovers`, {
      method: "POST", mode: "cors", headers: headers(),
      body: JSON.stringify({
        permitId: Number(id),
        incomingUserId: Number(handoverIncomingUserId),
        remarks: handoverRemarks,
        handoverTime: new Date().toISOString().slice(0, 19),
      }),
    })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success) {
          showSuccess("Shift handover recorded successfully.");
          setShiftHandovers((prev) => [j.data, ...prev]);
          setShowHandoverModal(false);
          setHandoverIncomingUserId("");
          setHandoverRemarks("");
        } else {
          showError(j.message || "Failed to record handover.");
        }
      })
      .catch(() => showError("Network error while recording handover."))
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const respondToHandover = async (handoverId, accepted, remark = "") => {
    if (!accepted && !remark.trim()) {
      showWarning("A remark is required when rejecting a shift handover.");
      return;
    }
    setButtonsDisabledWithLog(true);
    try {
      const resp = await fetch(
        `${restApiUrl}/api/shift-handovers/${handoverId}/accept?accepted=${accepted}&remark=${encodeURIComponent(remark)}`,
        { method: "POST", mode: "cors", headers: headers() },
      );
      const j = await resp.json();
      if (j && j.success) {
        showSuccess(accepted ? "Shift handover accepted." : "Shift handover rejected.");
        setShiftHandovers((prev) => prev.map((h) => (h.id === handoverId ? j.data : h)));
      } else {
        showError(j.error || "Failed to respond to shift handover.");
      }
    } catch (e) {
      showError("Network error while responding to shift handover.");
    } finally {
      setButtonsDisabledWithLog(false);
    }
  };

  useEffect(() => {
    // Fetch current user's department access list from backend
    const tryFetch = async (path) => {
      try {
        const res = await fetch(`${restApiUrl}${path}`, {
          method: "GET",
          mode: "cors",
          headers: headers(),
        });
        const j = await res.json();
        if (j && j.success && j.data) {
          const raw = j.data;
          if (Array.isArray(raw)) {
            const ids = raw
              .map((d) => {
                if (d == null) return null;
                if (typeof d === "object")
                  return d.departmentId || d.id || d.department || null;
                return d;
              })
              .filter((v) => v != null)
              .map((v) => String(v));
            setUserDeptAccess(ids);
          } else {
            setUserDeptAccess([]);
          }
          return true;
        }
      } catch (e) {
        // ignore
      }
      return false;
    };

    // try both possible paths: '/departmentusers' and '/api/departmentusers'
    (async () => {
      const ok = await tryFetch("/departmentusers/byuser");
    })();
  }, [id]);

  useEffect(() => {
    if (showAuditModal) {
      setAuditLogs([]);
      loadAuditLogs(0, false);
    }
  }, [showAuditModal]);

  const loadAuditLogs = (page = 0, append = false) => {
    setAuditLoading(true);
    fetch(
      `${restApiUrl}/api/audit-logs?entityType=Permit&entityId=${id}&page=${page}&size=10&sort=timestamp,desc`,
      {
        method: "GET",
        mode: "cors",
        headers: headers(),
      },
    )
      .then((res) => res.json())
      .then((j) => {
        if (j && j.success && j.data) {
          const content = j.data.content || [];
          if (append) setAuditLogs((prev) => [...prev, ...content]);
          else setAuditLogs(content);
          setAuditPage(page);
          setAuditHasMore(content.length === 10);
        }
      })
      .catch((err) => console.error("Failed to load audit logs", err))
      .finally(() => setAuditLoading(false));
  };

  const isBlockClearancesApproved = (b) => {
    try {
      // Clearances are permit-level now; consider all permit.clearanceDepartments
      const allClearances =
        permit && Array.isArray(permit.clearanceDepartments)
          ? permit.clearanceDepartments
          : groupedClearances[""] || [];
      if (!allClearances || allClearances.length === 0) return true;
      // Require each clearance to have an approver recorded (`approvedById`) before allowing review
      return allClearances.every((c) => {
        if (!c || !c.id) return true;
        return !!(
          c.approvedById ||
          (c.approvedBy && (c.approvedBy.id || c.approvedById))
        );
      });
    } catch (e) {
      return true;
    }
  };

  // Check if current user has one of the required roles for this permit-type block
  const userHasRequiredRole = (b) => {
    try {
      const req =
        (b && b.requiredRoles) ||
        (b && b.permitType && b.permitType.requiredRoles) ||
        [];
      if (!req || req.length === 0) return false;
      if (!user || !user.authorities) return false;
      return req.some((r) => user.authorities.includes(r));
    } catch (e) {
      return true;
    }
  };

  const getMergedBlockChecklist = (block, blockIndex = 0) => {
    const master =
      permitTypeChecklistMap && block && block.permitTypeId
        ? permitTypeChecklistMap[String(block.permitTypeId)] || []
        : [];
    const savedItems = Array.isArray(block?.checklistItems)
      ? block.checklistItems
      : [];

    const normalizedSaved = savedItems
      .map((ci, ciIdx) => {
        if (!ci) return null;
        const checklistObj =
          ci.checklist ||
          (ci.permitTypeChecklistId != null
            ? allChecklists.find(
              (item) =>
                String(item.id) === String(ci.permitTypeChecklistId),
            )
            : null);
        return {
          id:
            ci.id ||
            `saved-${block?.id || block?.permitTypeId || blockIndex}-${ciIdx}`,
          checklistItemId: ci.id || null,
          permitTypeChecklistId:
            ci.permitTypeChecklistId ||
            (checklistObj && checklistObj.id) ||
            null,
          text:
            ci.text ||
            (checklistObj &&
              (checklistObj.name || checklistObj.checklist || checklistObj.text)) ||
            "",
          mandatory:
            ci.mandatory !== undefined && ci.mandatory !== null
              ? !!ci.mandatory
              : !!ci.checked,
          checked:
            ci.checked !== undefined && ci.checked !== null
              ? !!ci.checked
              : ci.mandatory !== undefined && ci.mandatory !== null
                ? !!ci.mandatory
                : true,
          status:
            ci.status || (ci.checked || ci.mandatory ? "DONE" : "PENDING"),
          fields:
            ci.fields ||
            (checklistObj && checklistObj.fields) ||
            [],
          fieldsValues: ci.fieldsValues || {},
          checklist: checklistObj || ci.checklist || null,
        };
      })
      .filter(Boolean);

    const byChecklistId = new Map();
    normalizedSaved.forEach((item) => {
      if (item.permitTypeChecklistId != null) {
        byChecklistId.set(String(item.permitTypeChecklistId), item);
      }
    });

    const merged = (master || []).map((masterItem, masterIdx) => {
      const key = String(masterItem.id);
      const existing = byChecklistId.get(key);
      if (existing) {
        return {
          ...existing,
          text:
            existing.text ||
            masterItem.name ||
            masterItem.checklist ||
            masterItem.text ||
            "",
          fields:
            existing.fields && existing.fields.length > 0
              ? existing.fields
              : masterItem.fields || [],
          checklist: existing.checklist || masterItem,
        };
      }
      return {
        id: `master-${block?.id || block?.permitTypeId || blockIndex}-${masterIdx}`,
        checklistItemId: null,
        permitTypeChecklistId: masterItem.id,
        text:
          masterItem.name || masterItem.checklist || masterItem.text || "",
        mandatory: false,
        checked: false,
        status: "PENDING",
        fields: masterItem.fields || [],
        fieldsValues: {},
        checklist: masterItem,
      };
    });

    normalizedSaved.forEach((item) => {
      if (
        item.permitTypeChecklistId == null ||
        !master.some(
          (masterItem) =>
            String(masterItem.id) === String(item.permitTypeChecklistId),
        )
      ) {
        merged.push(item);
      }
    });

    return merged;
  };

  const updateBlockChecklistItem = (blockIndex, checklistKey, updates) => {
    setPermitTypeBlocks((prev) =>
      (prev || []).map((block, idx) => {
        if (idx !== blockIndex) return block;
        const merged = getMergedBlockChecklist(block, idx);
        const nextChecklist = merged.map((item) => {
          const itemKey =
            item.checklistItemId ||
            item.permitTypeChecklistId ||
            item.id;
          if (String(itemKey) !== String(checklistKey)) return item;
          const nextChecked =
            updates.checked !== undefined ? !!updates.checked : item.checked;
          return {
            ...item,
            ...updates,
            checked: nextChecked,
            mandatory: nextChecked,
            status:
              updates.status ||
              (nextChecked ? item.status || "DONE" : "PENDING"),
          };
        });
        return {
          ...block,
          checklistItems: nextChecklist,
        };
      }),
    );
  };

  const updateBlockChecklistFieldValue = (
    blockIndex,
    checklistKey,
    fieldName,
    value,
  ) => {
    setPermitTypeBlocks((prev) =>
      (prev || []).map((block, idx) => {
        if (idx !== blockIndex) return block;
        const merged = getMergedBlockChecklist(block, idx);
        const nextChecklist = merged.map((item) => {
          const itemKey =
            item.checklistItemId ||
            item.permitTypeChecklistId ||
            item.id;
          if (String(itemKey) !== String(checklistKey)) return item;
          return {
            ...item,
            fieldsValues: {
              ...((item && item.fieldsValues) || {}),
              [fieldName]: value,
            },
          };
        });
        return {
          ...block,
          checklistItems: nextChecklist,
        };
      }),
    );
  };

  const buildReviewChecklistPayload = () =>
    (permitTypeBlocks || []).flatMap((block, blockIndex) =>
      getMergedBlockChecklist(block, blockIndex).map((item) => ({
        id: item.checklistItemId || null,
        permitId: Number(id),
        permitPermitTypeId: block.permitTypeId || null,
        permitTypeChecklistId: item.permitTypeChecklistId || null,
        text: item.text || "",
        mandatory: !!item.checked,
        checked: !!item.checked,
        status: item.checked ? item.status || "DONE" : "PENDING",
        fieldsValues: item.fieldsValues || {},
      })),
    );

  const handleAction = (action, remark, until, checkedIds) => {
    let url = `/api/permits/${id}/${action}?`;
    if (remark) url += `&remark=${encodeURIComponent(remark)}`;
    if (until) {
      // Use specific param names for suspend/extend to be explicit
      if (action === "suspend")
        url += `&suspendUntil=${encodeURIComponent(until)}`;
      else if (action === "extend")
        url += `&extendUntil=${encodeURIComponent(until)}`;
      else url += `&until=${encodeURIComponent(until)}`;
    }
    if (action === "close" && checkedIds && checkedIds.length > 0) {
      url += `&checkedIds=${encodeURIComponent(checkedIds.join(","))}`;
    }

    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}` + url, {
      method: "POST",
      mode: "cors",
      headers: headers(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.success) {
          // If extend action returned the updated full permit, update state in-place
          if (action === "extend" && data.data) {
            const full = data.data;
            if (full.permit) {
              setPermit(full.permit);

              // Rebuild clearance maps from returned permit
              if (Array.isArray(full.permit.clearanceDepartments)) {
                const map = {};
                const serverMap = {};
                const remarksMap = {};
                full.permit.clearanceDepartments.forEach((c) => {
                  if (c && c.id) {
                    map[c.id] = c.approved == null ? null : !!c.approved;
                    serverMap[c.id] = !!c.approved;
                    if (c.remark) remarksMap[c.id] = c.remark;
                  }
                });
                setClearanceEdits(map);
                setClearanceServerApproved(serverMap);
                setClearanceRemarks(remarksMap);
              }

              if (full.permitTypeBlocks)
                setPermitTypeBlocks(full.permitTypeBlocks);
              if (full.checklistItems) setChecklistItems(full.checklistItems);
            }
          } else {
            // fallback: full reload for other actions
            window.location.reload();
          }
        } else {
          // Handle error response from backend
          const errorMessage = data && data.error
            ? data.error
            : `Failed to ${action} permit`;
          showError(errorMessage);
        }
      })
      .catch((error) => {
        showError(`Network error: ${error.message || 'Failed to perform action'}`);
      })
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const submitReceiverDepartmentReview = async () => {
    setButtonsDisabledWithLog(true);
    if (hodEditApproved === null) {
      setButtonsDisabledWithLog(false);
      showWarning("Please complete the issuer department review by selecting Approve or Reject.");
      return;
    }
    if (
      hodEditApproved === false &&
      (!hodEditRemark || hodEditRemark.trim() === "")
    ) {
      setHodValidationError(true);
      showWarning("Please provide an issuer department review remark for rejection");
      return;
    }

    const checklistPayload = buildReviewChecklistPayload();
    try {
      const saveResp = await fetch(`${restApiUrl}/api/permits/${id}/save-review`, {
        method: "POST",
        mode: "cors",
        headers: headers(),
        body: JSON.stringify({
          checklistItems: checklistPayload,
          remark: hodEditRemark || null,
        }),
      });
      const saveJson = await saveResp.json();
      if (!saveJson.success) {
        throw new Error(saveJson.error || "Failed to save checklist review");
      }

      const approveUrl = `${restApiUrl}/api/permits/${id}/hod-approve?approved=${hodEditApproved}&remark=${encodeURIComponent(hodEditRemark || "")}`;
      const approveResp = await fetch(approveUrl, {
        method: "POST",
        mode: "cors",
        headers: headers(),
      });
      const approveJson = await approveResp.json();
      if (!approveJson.success) {
        throw new Error(approveJson.error || "Failed to submit HOD review");
      }
      window.location.reload();
    } catch (e) {
      throw e;
    } finally {
      setButtonsDisabledWithLog(false);
    }
  };

  const userHasDeptAccess = (dept) => {
    if (!dept || !userDeptAccess) return false;
    const id = (dept && (dept.id || dept.departmentId)) || dept;
    return userDeptAccess.map(String).includes(String(id));
  };

  const userCanApproveClearance = (c) => {
    try {
      if (!user) return false;
      if (
        !user.authorities ||
        !user.authorities.includes(ptw_can_review_clearance_department)
      )
        return false;
      const deptId =
        c &&
        (c.departmentId || (c.department && c.department.id) || c.department);
      return !!userHasDeptAccess(deptId);
    } catch (e) {
      return false;
    }
  };

  const formatRiskById = (riskAssessment) => {
    if (!riskAssessment) return "N/A";
    const rm = riskAssessment && riskAssessment.overallRisk;
    return `Likelihood: ${rm.likelihood}, Severity: ${rm.severity} (${rm.riskLevel})`;
  };

  // Determine which checklist items should be used when closing a permit.
  // Prefer permit-type block checklists when available, otherwise fall
  // back to the top-level `checklistItems` returned by the API.
  const getCloseChecklistItems = () => {
    if (
      permitTypeBlocks &&
      Array.isArray(permitTypeBlocks) &&
      permitTypeBlocks.length > 0
    ) {
      return permitTypeBlocks.flatMap((b) =>
        (b.checklistItems || []).map((ci) => ci).filter(Boolean),
      );
    }
    return checklistItems || [];
  };

  const openRemarkModal = (action) => {
    setRemarkAction(action);
    setRemarkText("");
    setRemarkDate("");
    if (action === "close") {
      const map = {};
      const items = getCloseChecklistItems();
      (items || []).forEach((c) => {
        map[c.id] = c && c.status === "DONE";
      });
      setChecklistReview(map);
    }
    setShowRemarkModal(true);
  };

  const confirmRemark = () => {
    if (!remarkAction) return;

    // For suspend/extend build ISO until value
    let until = null;
    if (
      (remarkAction === "suspend" || remarkAction === "extend") &&
      remarkDate
    ) {
      try {
        until = remarkDate.replace("T", " ").slice(0, 16);
      } catch (e) {
        until = remarkDate;
      }
    }

    // Validate extend date is after scheduleEnd and in the future
    if (remarkAction === "extend") {
      if (!remarkDate) {
        showWarning("Please select an extend until date.");
        return;
      }
      const extendDate = new Date(remarkDate);

      if (extendDate <= new Date()) {
        showWarning("The extend until date must be a future date and time.");
        return;
      }

      if (permit && permit.scheduleEnd) {
        const scheduleEndDate = new Date(permit.scheduleEnd);

        if (extendDate <= scheduleEndDate) {
          showWarning(
            "The extend until date must be after the permit's scheduled end date."
          );
          return;
        }
      }
    }

    // Validate suspend date is between scheduleStart and scheduleEnd
    if (remarkAction === "suspend") {
      if (!remarkDate) {
        showWarning("Please select a suspend until date.");
        return;
      }
      if (permit) {
        const suspendDate = new Date(remarkDate);

        // Check if suspend date is within schedule range
        if (permit.scheduleStart) {
          const scheduleStartDate = new Date(permit.scheduleStart);
          if (suspendDate < scheduleStartDate) {
            showWarning(
              "The suspend until date must be on or after the permit's scheduled start date."
            );
            return;
          }
        }

        if (permit.scheduleEnd) {
          const scheduleEndDate = new Date(permit.extendUntil) || new Date(permit.scheduleEnd);
          if (suspendDate > scheduleEndDate) {
            showWarning(
              "The suspend until date must be on or before the permit's scheduled end date."
            );
            return;
          }
        }

        // Check if suspend date is before extend until date (if extended)
        if (permit.extendUntil) {
          const extendUntilDate = new Date(permit.extendUntil);
          if (suspendDate >= extendUntilDate) {
            showWarning(
              "The suspend until date must be before the extend until date."
            );
            return;
          }
        }
      }
    }

    if (remarkText == null || remarkText.trim() === "") {
      showWarning("Please enter a remark before confirming.");
      return;
    }

    // If closing, validate mandatory checklist items are marked done
    if (remarkAction === "close") {
      const missingMandatory = (getCloseChecklistItems() || []).some((c) =>
        c && c.mandatory ? !checklistReview[c.id] : false,
      );
      if (missingMandatory) {
        showWarning(
          "Please complete all mandatory checklist items before closing the permit.",
        );
        return; // keep modal open for user to correct
      }

      const checked = Object.keys(checklistReview).filter(
        (k) => checklistReview[k],
      );
      setShowRemarkModal(false);
      handleAction(remarkAction, remarkText, until, checked);
    } else {
      setShowRemarkModal(false);
      handleAction(remarkAction, remarkText, until);
    }

    // Clear prefilled return comment after executing return action
    if (remarkAction === "return") {
      setReturnComment("");
    }
  };

  return (
    <div
      id="permit-detail"
      className="max-w-full bg-white p-2 sm:p-2 lg:p-6 rounded-lg shadow-md m-1 sm:m-2 overflow-y-scroll overflow-x-hidden"
      style={{ maxHeight: "calc(100vh - 80px)" }}
    >


      {permit && (
        <>
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div id="permit-detail-title-row" className="flex items-center gap-2 flex-wrap min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-gray-800">
                  Permit Details
                </h1>
                <span className="pd-mono text-xs text-gray-500">{formatPermitTrackingId(permit)}</span>
                <span
                  className={`pd-pill ${permit.status === "CLOSED"
                    ? "pd-pill-neutral"
                    : permit.status === "CANCELLED" || permit.status === "REJECTED"
                      ? "pd-pill-crit"
                      : permit.status === "SUBMITTED" || permit.status === "CLEARANCE_DEPARTMENT_REVIEW" || permit.status === "PERMIT_TYPE_REVIEW"
                        ? "pd-pill-warn"
                        : "pd-pill-info"
                    }`}
                >
                  {permit.status}
                </span>
              </div>
              <div id="permit-detail-actions" className="flex-shrink-0 flex items-center gap-2">
                <button
                  onClick={() => navigate("/permits")}
                  disabled={buttonsDisabled}
                  className="px-3 py-1.5 text-sm font-medium bg-gray-200 text-gray-800 rounded hover:bg-gray-500 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back to list
                </button>
                {user &&
                  [
                    ptw_can_create_permit,
                    ptw_can_review_competent_person,
                    ptw_can_approve_hot_work,
                    ptw_can_approve_cold_work,
                    ptw_can_approve_electrical_work,
                    ptw_can_approve_confined_space_entry,
                    ptw_can_approve_work_at_height,
                    ptw_can_approve_excavation_work,
                    ptw_can_approve_chemical_handling,
                  ].some((authority) =>
                    user.authorities === undefined
                      ? false
                      : user.authorities.includes(authority),
                  ) && (
                    <button
                      onClick={() => setShowAuditModal(true)}
                      disabled={buttonsDisabled}
                      className="px-3 py-1.5 text-sm font-medium bg-blue-800 text-white rounded hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Audit Log
                    </button>
                  )}
                <PermitPdf permit={permit} />
              </div>
            </div>
          </div>

          <div
            className="max-w-5xl mx-auto pd-summary-strip mb-3"
            style={{ gridTemplateColumns: "repeat(8, minmax(0, 1fr))" }}
          >
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">ID</div><div className="v pd-mono">{permit.id}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}>
              <div className="k">Tracking ID</div>
              <div className="v pd-mono" style={{ fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}>
                {formatPermitTrackingId(permit)}
              </div>
            </div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Site</div><div className="v" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{permit.siteName || "N/A"}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Issuer Dept</div><div className="v" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{permit.issuerDepartmentName || "N/A"}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Receiver Dept</div><div className="v" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{permit.receiverDepartmentName || "N/A"}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Priority</div><div className="v">{permit.priority || "N/A"}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Schedule</div><div className="v pd-mono" style={{ fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}>
              {permit.scheduleStart ? permit.scheduleStart.replace("T", " ").slice(0, 16) : "N/A"} → {permit.scheduleEnd ? permit.scheduleEnd.replace("T", " ").slice(0, 16) : "N/A"}
            </div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Actual</div><div className="v pd-mono" style={{ fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}>
              {permit.actualStart ? permit.actualStart.replace("T", " ").slice(0, 16) : "N/A"} → {permit.actualEnd ? permit.actualEnd.replace("T", " ").slice(0, 16) : "N/A"}
            </div></div>
          </div>

          {permit && ["CLOSED", "CANCELLED"].includes(permit.status) && permit.retentionExpiresAt && (
            <div className="pd-notice mb-3 max-w-screen-lg mx-auto">
              <span className="font-bold">🗄</span>
              <span>
                This permit record is under <strong>mandatory retention</strong> and cannot be deleted before{" "}
                <strong>{permit.retentionExpiresAt.replace("T", " ").slice(0, 10)}</strong> (1-year regulatory requirement).
              </span>
            </div>
          )}
          {/* ─── SIMOPS Conflict Warning Banner ─── */}
          {simopsConflicts.length > 0 && permit && ["DRAFT", "SUBMITTED", "COMPETENT_PERSON_APPROVAL", "ISSUER_DEPARTMENT_HOD_REVIEW", "CLEARANCE_DEPARTMENT_REVIEW", "PERMIT_TYPE_REVIEW", "APPROVED", "ISSUED", "ACTIVE"].includes(permit.status) && (
            <div className="bg-orange-50 border border-orange-400 rounded-lg p-2 mb-4 max-w-screen-lg mx-auto">
              <div className="flex items-start gap-2">
                <span className="text-orange-600 font-bold text-lg">⚠</span>
                <div>
                  <p className="font-semibold text-orange-800">SIMOPS Conflict — Simultaneous Operations Detected</p>
                  <p className="text-sm text-orange-700 mt-1">
                    The following active/issued permits overlap with this permit's schedule and location:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {simopsConflicts.map((c) => (
                      <li key={c.permitId} className="text-sm text-orange-800">
                        • Permit <strong>#{c.permitId}</strong> — {c.status} at {c.locationName}
                        {c.scheduleStart && ` (${c.scheduleStart.replace("T", " ").slice(0, 16)} → ${c.scheduleEnd ? c.scheduleEnd.replace("T", " ").slice(0, 16) : "?"})`}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* ─── Gas Test Mandatory Warning Banner ─── */}
          {permit && ["ISSUED", "ACTIVE"].includes(permit.status) &&
            permit.permitTypeBlocks && permit.permitTypeBlocks.some(b => b.gasTestMandatory) && (() => {
              const now = new Date();
              const hasValid = gasTests.length > 0 &&
                (!gasTests[0].validUntil || new Date(gasTests[0].validUntil) >= now);
              if (hasValid) return null;
              const msg = gasTests.length === 0
                ? "No gas test has been recorded for this permit."
                : `Gas test expired at ${gasTests[0].validUntil.replace("T", " ").slice(0, 16)}.`;
              const action = permit.status === "ISSUED"
                ? "A valid gas test is required before this permit can be activated."
                : "Fresh testing required immediately or suspend work.";
              return (
                <div className="max-w-screen-lg mx-auto mb-4">
                  <div className="bg-red-50 border-l-4 border-red-600 p-2 mb-2 rounded flex items-start gap-3">
                    <span className="text-red-600 text-xl font-bold">⚠</span>
                    <div>
                      <p className="font-semibold text-red-800 m-0">Gas Test Required</p>
                      <p className="text-red-700 text-sm m-0">{msg} {action}</p>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* ─── Confined Space Box-Up Clearance ─── */}
          {permit && permit.permitTypeBlocks && permit.permitTypeBlocks.some(b => b.permitTypeCode === "CSE") && (
            permit.boxUpClearedAt ? (
              <div className="bg-green-50 border border-green-300 rounded-lg p-2 mb-4 max-w-screen-lg mx-auto text-sm text-green-800">
                <strong>✓ Confined Space Box-Up Clearance:</strong> recorded by <strong>{permit.boxUpClearedBy}</strong> at{" "}
                {permit.boxUpClearedAt.replace("T", " ").slice(0, 16)}
                {permit.boxUpRemark && <span> — <em>{permit.boxUpRemark}</em></span>}
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-400 rounded-lg p-2 mb-4 max-w-screen-lg mx-auto">
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold text-lg">⚠</span>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">Confined Space — Box-Up Clearance Required</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Before closing this Confined Space permit, a responsible person must certify that all workers and tools have been removed from the confined space (box-up clearance).
                    </p>
                    {["ACTIVE", "SUSPENDED"].includes(permit.status) && (
                      <button
                        onClick={() => setShowBoxUpModal(true)}
                        disabled={buttonsDisabled}
                        className="mt-3 bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Record Box-Up Clearance
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          )}

          {(permit.status === "SUSPENDED" || permit.status === "ISSUED") && (() => {
            try {
              const effectiveEnd = permit.extendUntil || permit.scheduleEnd;
              if (effectiveEnd) {
                const endDate = new Date(effectiveEnd);
                const now = new Date();
                if (now > endDate) {
                  return (
                    <div className="max-w-screen-lg mx-auto mb-4">
                      <div className="bg-red-50 border-l-4 border-red-600 p-2 rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="text-red-600 font-bold text-lg mr-2">⚠</span>
                            <span className="text-red-800 font-semibold">
                              Schedule time expired - Extension required before activation
                            </span>
                          </div>
                          <button
                            onClick={() => openRemarkModal("extend")}
                            disabled={buttonsDisabled}
                            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 ml-4 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Extend Permit
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }
              }
            } catch (e) {
              // Ignore date parsing errors
            }
            return null;
          })()}

          <div
            className="max-w-5xl mx-auto pd-summary-strip mb-3"
            style={{ gridTemplateColumns: "repeat(8, minmax(0, 1fr))" }}
          >
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Location</div><div className="v" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{permit.locationName || "N/A"}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}>
              <div className="k">Status</div>
              <div
                className={`v ${permit.status === "CLOSED"
                  ? "text-gray-600"
                  : permit.status === "CANCELLED" || permit.status === "REJECTED"
                    ? "text-red-600"
                    : permit.status === "SUBMITTED" || permit.status === "CLEARANCE_DEPARTMENT_REVIEW" || permit.status === "PERMIT_TYPE_REVIEW"
                      ? "text-amber-600"
                      : "text-blue-600"
                  }`}
                style={{ fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {permit.status}
              </div>
            </div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Auto Reject on Schedule Time</div><div className="v">{permit.permitAutoRejectOnScheduleTime ? "Yes" : "No"}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Requires Periodic Revalidation</div><div className="v" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{permit.revalidationRequired ? `Yes — every ${permit.revalidationIntervalHours || 8} hours` : "No"}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Suspend Until</div><div className="v pd-mono" style={{ fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}>{permit.suspendUntil ? permit.suspendUntil.replace("T", " ").slice(0, 16) : "N/A"}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Extended Until</div><div className="v pd-mono" style={{ fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}>{permit.extendUntil ? permit.extendUntil.replace("T", " ").slice(0, 16) : "N/A"}</div></div>
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Created By / Updated By</div><div className="v" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{permit.createdBy || "N/A"} → {permit.updatedBy || "N/A"}</div></div>
            {permit.suspendedAt && (
              <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Suspended At</div><div className="v pd-mono" style={{ fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}>{permit.suspendedAt.replace("T", " ").slice(0, 16)}</div></div>
            )}
            {permit.suspendedBy && (
              <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Suspended By</div><div className="v" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{permit.suspendedBy}</div></div>
            )}
            {permit.resumedAt && (
              <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Resumed At</div><div className="v pd-mono" style={{ fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}>{permit.resumedAt.replace("T", " ").slice(0, 16)}</div></div>
            )}
            {permit.resumedBy && (
              <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Resumed By</div><div className="v" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{permit.resumedBy}</div></div>
            )}
            <div className="pd-summary-cell" style={{ minWidth: 0 }}><div className="k">Date Created / Updated</div><div className="v pd-mono" style={{ fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}>{permit.createdDate ? permit.createdDate.replace("T", " ").slice(0, 16) : "N/A"} → {permit.updatedAt ? permit.updatedAt.replace("T", " ").slice(0, 16) : "N/A"}</div></div>
            {permit.resumptionRemark && (
              <div className="pd-summary-cell" style={{ minWidth: 0, gridColumn: "1 / -1" }}><div className="k">Resumption Sign-off</div><div className="v" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{permit.resumptionRemark}</div></div>
            )}
          </div>

          {/* Existing comments (show history) */}
          {permit && permit.comments && permit.comments.trim() !== "" && (
            <div className="w-full mb-3 max-w-screen-lg mx-auto">
              <div className="text-sm font-bold mb-1">
                Return Comments History
              </div>
              <div className="whitespace-pre-wrap bg-gray-50 border rounded p-2 text-sm">
                {permit.comments}
              </div>
            </div>
          )}
          <div className="pd-card mt-3 max-w-screen-lg mx-auto">
            <div className="pd-card-head"><h2>Job Description</h2></div>
            <div className="pd-card-body text-sm text-gray-700 whitespace-pre-wrap">
              {permit.jobDescription || permit.title || "N/A"}
            </div>
          </div>

          {permitTypeBlocks && permitTypeBlocks.length > 0 && (
            <div className="w-full pt-1 max-w-screen-lg mx-auto">
              <h3 className="pd-section-title">
                {permit && permit.status === "PERMIT_TYPE_REVIEW"
                  ? "Review Permit Types"
                  : "Permit Types"}
                {permit && permit.status === "PERMIT_TYPE_REVIEW" && (
                  <span className="count">
                    {(permitTypeBlocks || []).filter((b) => b.approved).length} / {(permitTypeBlocks || []).length} approved
                  </span>
                )}
              </h3>

              <div className="grid grid-cols-1 gap-3">
                {(permitTypeBlocks || []).map((b, idx) => (
                  <div key={b.id || idx} className="pd-card">
                    <div className="pd-card-head" style={{ justifyContent: "flex-start", gap: "10px", flexWrap: "wrap" }}>
                      <span
                        className="pd-pill"
                        style={{ background: b.permitTypeColor || "#6b7280", color: "#fff" }}
                      >
                        {b.permitTypeName || `Type ${idx + 1}`}
                      </span>
                      <span className="text-xs text-gray-500">
                        Residual Risk: <b className="text-gray-800">{b.residualRiskLevel || "N/A"}</b>
                      </span>
                    </div>
                    <div className="pd-card-body">
                      <div className="flex-1">
                        <div className="pd-kv-grid span-3" style={{ marginBottom: "10px" }}>
                          <Field label="Contractor" value={b.contractorName || "N/A"} />
                          <Field label="Equipment ID" value={b.equipmentId || "N/A"} />
                          <Field label="Work Order ID" value={b.externalWorkOrderId || "N/A"} />
                        </div>
                        {/* reviewer remark moved below so it appears after clearances and approval */}
                        {/* Permit-type checklist: prefer block-specific items, else show master list */}
                        {(() => {
                          const normalizeItem = (ci, ciIdx) => {
                            if (!ci) return null;
                            const id =
                              ci.id ||
                              ci.permitTypeChecklistId ||
                              (ci.checklist && ci.checklist.id) ||
                              `b-${b.id || idx}-c-${ciIdx}`;
                            const checklistObj = ci.checklist || null;
                            const text =
                              ci.text ||
                              (checklistObj &&
                                (checklistObj.name ||
                                  checklistObj.checklist ||
                                  checklistObj.text)) ||
                              "";
                            return {
                              id,
                              checklistItemId: ci.checklistItemId || ci.id || null,
                              permitTypeChecklistId:
                                ci.permitTypeChecklistId ||
                                (ci.checklist && ci.checklist.id) ||
                                null,
                              text,
                              mandatory: !!ci.mandatory,
                              checked:
                                ci.checked !== undefined && ci.checked !== null
                                  ? !!ci.checked
                                  : !!ci.mandatory,
                              status:
                                ci.status ||
                                (ci.checked ? "DONE" : "PENDING"),
                              fields:
                                ci.fields ||
                                (checklistObj && checklistObj.fields) ||
                                [],
                              fieldsValues: ci.fieldsValues || {},
                            };
                          };

                          let blockChecklist = getMergedBlockChecklist(
                            b,
                            idx,
                          ).map((ci, ciIdx) =>
                            normalizeItem(
                              {
                                ...ci,
                                checked: ci.checked,
                                mandatory: ci.mandatory,
                              },
                              ciIdx,
                            ),
                          );
                          const showOnlyRequiredChecklist =
                            permit &&
                            permit.status !==
                            "ISSUER_DEPARTMENT_HOD_REVIEW";

                          if (showOnlyRequiredChecklist) {
                            blockChecklist = blockChecklist.filter(
                              (ci) => ci && ci.mandatory,
                            );
                          }

                          if (!blockChecklist || blockChecklist.length === 0)
                            return null;
                          return (
                            <div className="mt-3">
                              <div className="pd-field-label mb-1">
                                Checklist
                              </div>
                              <div>
                                {blockChecklist.map((ci, ciIdx) => {
                                  const checklistKey =
                                    ci.checklistItemId ||
                                    ci.permitTypeChecklistId ||
                                    ci.id ||
                                    `${idx}-${ciIdx}`;
                                  const isHodChecklistEditable =
                                    permit &&
                                    permit.status ===
                                    "ISSUER_DEPARTMENT_HOD_REVIEW" &&
                                    hasIssuerDepartmentHOD;
                                  return (
                                    <div
                                      key={ci.id}
                                      className="flex items-start justify-between gap-2"
                                      style={{ padding: "7px 0", borderTop: ciIdx === 0 ? "none" : "1px solid var(--pd-line)" }}
                                    >
                                      <div className="flex-1 text-sm flex items-start gap-2 min-w-0" style={{ flexWrap: "wrap" }}>
                                        {isHodChecklistEditable && (
                                          <input
                                            type="checkbox"
                                            checked={!!ci.checked}
                                            className="mt-0.5"
                                            onChange={(e) =>
                                              updateBlockChecklistItem(
                                                idx,
                                                checklistKey,
                                                {
                                                  checked: e.target.checked,
                                                  status: e.target.checked
                                                    ? "DONE"
                                                    : "PENDING",
                                                },
                                              )
                                            }
                                          />
                                        )}
                                        <span>{ci.text}</span>
                                        <div className="w-full">
                                          {/* Inline badges for fields or fieldsValues */}
                                          {ci.fields && ci.fields.length > 0 ? (
                                            <div className="flex flex-wrap gap-2 items-center mt-1">
                                              {(ci.fields || []).map((f) => {
                                                const fv =
                                                  ci.fieldsValues || {};
                                                const tryKeys = [
                                                  f.fieldName,
                                                  f.label,
                                                  (
                                                    f.fieldName || ""
                                                  ).toLowerCase(),
                                                  (f.label || "").toLowerCase(),
                                                ];
                                                let val = undefined;
                                                for (const k of tryKeys) {
                                                  if (
                                                    k !== undefined &&
                                                    fv[k] !== undefined
                                                  ) {
                                                    val = fv[k];
                                                    break;
                                                  }
                                                }
                                                if (
                                                  val === undefined &&
                                                  fv &&
                                                  typeof fv === "object"
                                                ) {
                                                  if (
                                                    f.label &&
                                                    fv[f.label] !== undefined
                                                  )
                                                    val = fv[f.label];
                                                }
                                                const isEditableField =
                                                  isHodChecklistEditable &&
                                                  !!ci.checked;
                                                return (
                                                  <div
                                                    key={f.id || f.fieldName}
                                                    className="text-xs px-2 py-1 rounded inline-flex items-center gap-1.5"
                                                    style={{ background: "var(--pd-surface-2)", border: "1px solid var(--pd-line)", width: "fit-content" }}
                                                  >
                                                    <span className="font-semibold text-gray-600 whitespace-nowrap">
                                                      {f.label || f.fieldName}:
                                                    </span>
                                                    {isEditableField ? (
                                                      f.fieldType === "BOOLEAN" ? (
                                                        <select
                                                          value={
                                                            val !== undefined &&
                                                              val !== null
                                                              ? String(val)
                                                              : ""
                                                          }
                                                          onChange={(e) =>
                                                            updateBlockChecklistFieldValue(
                                                              idx,
                                                              checklistKey,
                                                              f.fieldName,
                                                              e.target.value,
                                                            )
                                                          }
                                                          className="border rounded px-1 py-0.5 bg-white text-xs flex-shrink-0"
                                                          style={{ width: "auto" }}
                                                        >
                                                          <option value="">
                                                            Select
                                                          </option>
                                                          <option value="true">
                                                            Yes
                                                          </option>
                                                          <option value="false">
                                                            No
                                                          </option>
                                                        </select>
                                                      ) : (
                                                        <input
                                                          type={
                                                            f.fieldType === "NUMBER"
                                                              ? "number"
                                                              : "text"
                                                          }
                                                          value={
                                                            val !== undefined &&
                                                              val !== null
                                                              ? String(val)
                                                              : ""
                                                          }
                                                          onChange={(e) =>
                                                            updateBlockChecklistFieldValue(
                                                              idx,
                                                              checklistKey,
                                                              f.fieldName,
                                                              e.target.value,
                                                            )
                                                          }
                                                          className="border rounded px-1 py-0.5 bg-white text-xs flex-shrink-0"
                                                          style={{ width: `${Math.max(8, String(val ?? "").length + 2)}ch`, minWidth: "4.5rem" }}
                                                        />
                                                      )
                                                    ) : (
                                                      <span>
                                                        {val === undefined || val === null || val === ""
                                                          ? "-"
                                                          : f.fieldType === "BOOLEAN"
                                                            ? (String(val) === "true" ? "Yes" : "No")
                                                            : String(val)}
                                                      </span>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : ci.fieldsValues &&
                                            Object.keys(ci.fieldsValues || {})
                                              .length > 0 ? (
                                            <div className="flex flex-wrap gap-2 items-center">
                                              {Object.keys(
                                                ci.fieldsValues || {},
                                              ).map((k) => (
                                                <div
                                                  key={k}
                                                  className="text-xs px-2 py-1 rounded"
                                                  style={{ background: "var(--pd-surface-2)", border: "1px solid var(--pd-line)" }}
                                                >
                                                  <span className="font-semibold mr-1 text-gray-600">
                                                    {k}:
                                                  </span>
                                                  <span>
                                                    {String(ci.fieldsValues[k])}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                      <div className="flex-shrink-0">
                                        {ci.mandatory ? (
                                          <span className="pd-pill pd-pill-warn">Required</span>
                                        ) : (
                                          <span className="pd-pill pd-pill-neutral">Optional</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                        {/* Competent Person approval for Confined Space Entry */}
                        {permit &&
                          b.permitTypeName &&
                          b.permitTypeName
                            .toLowerCase()
                            .includes("confined") && (
                            <div className="mt-3 border rounded p-2 bg-white">
                              <div className="text-sm font-bold mb-2">
                                Competent Person
                              </div>
                              {permit.status ===
                                "COMPETENT_PERSON_APPROVAL" &&
                                user &&
                                [ptw_can_review_competent_person].some(
                                  (authority) =>
                                    user.authorities === undefined
                                      ? false
                                      : user.authorities.includes(authority),
                                ) ? (
                                <div className="text-xs text-gray-500 italic">
                                  Awaiting your review — name &amp; acceptance are at the bottom of the page.
                                </div>
                              ) : (
                                <div className="text-sm text-gray-600">
                                  Competent Person name:{" "}
                                  {b.competentPersonName || "N/A"} • Acceptance:{" "}
                                  {b.competentPersonApproved == null && b.competentPersonApproved == undefined
                                    ? "Pending"
                                    : b.competentPersonApproved ? "Approved" : "Rejected"}
                                </div>
                              )}
                            </div>
                          )}

                        {/* Medical Fitness Certificate — WAH blocks only */}

                        {/* While actively under review, Reviewer Remark + Acceptance render
                            once at the bottom of the page (just above Submit Review) instead
                            of here — see the block rendered before the action button bar. */}
                        {permit && permit.status !== "PERMIT_TYPE_REVIEW" && (
                          <div className="flex flex-col sm:flex-row gap-3 mt-4 items-start">
                            <div className="flex-1 w-full">
                              <div className="pd-field-label mb-1">
                                Reviewer Remark
                              </div>
                              <div className="text-sm text-gray-800 border rounded-md p-2 bg-white">
                                {b.reviewerRemark ? (
                                  <pre className="whitespace-pre-wrap m-0">
                                    {b.reviewerRemark}
                                  </pre>
                                ) : (
                                  "-"
                                )}
                              </div>
                            </div>
                            <div className="sm:w-64 w-full flex-shrink-0">
                              <div className="pd-field-label mb-1">Acceptance</div>
                              <div className="border rounded-md p-2 bg-white">
                                <span
                                  className={`pd-pill ${b.approved == null && b.approved == undefined
                                    ? "pd-pill-neutral"
                                    : b.approved ? "pd-pill-ok" : "pd-pill-crit"
                                    }`}
                                >
                                  {b.approved == null && b.approved == undefined
                                    ? "Pending"
                                    : b.approved ? "Approved" : "Rejected"}
                                </span>
                                {b.approvedByName && (
                                  <div className="text-xs text-gray-600 mt-1">
                                    By: {b.approvedByName}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>


            </div>
          )}

          <div className="pt-1 mb-4 max-w-screen-lg mx-auto">
            <h3 className="pd-section-title">
              Risk Assessment &amp; Job Safety Analysis
              <span className="count">{formatRiskById(riskAssessment)}</span>
            </h3>
            {!riskItems || riskItems.length === 0 ? (
              <div className="pd-notice">
                <span>⚠</span>
                <span><strong>No risk assessment found</strong></span>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {riskItems.map((ri, idx) => (
                  <div
                    key={ri.id || idx}
                    className="pd-card"
                    style={{ borderLeft: "3px solid var(--pd-info)" }}
                  >
                    <div className="pd-card-body" style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <div className="pd-mono" style={{ fontSize: "10.5px", color: "var(--pd-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Step {ri.stepNo || idx + 1}
                      </div>
                      <div className="text-sm font-semibold text-gray-800 break-words">
                        {ri.taskDescription || "—"}
                      </div>
                      <div className="text-xs text-gray-500">
                        <span className="font-semibold text-gray-600">Hazard: </span>
                        {ri.hazardName || "—"}
                      </div>
                      <div className="text-xs">
                        <div className="font-semibold text-gray-600" style={{ fontSize: "10.5px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Control Measures</div>
                        {(ri.controlIds || []).length > 0 || ri.otherControlText ? (
                          <ul className="list-disc ml-4 mt-1 space-y-0.5 text-gray-700">
                            {(ri.controlIds || []).map((cid) => {
                              const name = ri.controlNames?.length > 0
                                ? ri.controlNames[ri.controlIds.indexOf(cid)]
                                : `ID ${cid}`;
                              return <li key={cid}>{name}</li>;
                            })}
                            {ri.otherControlText && (
                              <li>{ri.otherControlText}</li>
                            )}
                          </ul>
                        ) : (
                          <span className="text-gray-400"> —</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 border-t pt-1.5 mt-1">
                        <span className="font-semibold text-gray-600">Responsible: </span>
                        {ri.responsiblePersonName || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {permit && (!hasIssuerDepartmentHOD ||
            permit.status !== "ISSUER_DEPARTMENT_HOD_REVIEW") && (
              <div className="pd-card mt-3 max-w-screen-lg mx-auto">
                <div className="pd-card-head"><h2>Issuer Dept HOD Approval</h2></div>
                <div className="pd-card-body text-sm flex flex-wrap items-center gap-2">
                  <span
                    className={`pd-pill ${permit.hodApproved === null || permit.hodApproved === undefined
                      ? "pd-pill-neutral"
                      : permit.hodApproved
                        ? "pd-pill-ok"
                        : "pd-pill-crit"
                      }`}
                  >
                    {permit.hodApproved === null || permit.hodApproved === undefined
                      ? "Pending"
                      : permit.hodApproved
                        ? "Approved"
                        : "Rejected"}
                  </span>
                  {permit.hodApprovedBy && (
                    <span className="text-gray-600">by <b className="text-gray-900 font-medium">{permit.hodApprovedBy}</b></span>
                  )}
                  {permit.hodApprovalRemark && (
                    <span className="text-gray-600">· Remark: <span className="whitespace-pre-wrap text-gray-800">{permit.hodApprovalRemark}</span></span>
                  )}
                </div>
              </div>
            )}

          {permit && Array.isArray(permit.clearanceDepartments) && (
            <div className="w-full  pt-4 mb-2">
              <div className="mt-3">
                <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-2 px-2 sm:px-4">
                  <h3 className="pd-section-title" style={{ margin: 0 }}>Clearance — by Cycle</h3>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setCycleFilter("all")}
                      className={`pd-pill ${cycleFilter === "all" ? "pd-pill-info" : "pd-pill-neutral"}`}
                      style={{ cursor: "pointer" }}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setCycleFilter("pending")}
                      className={`pd-pill ${cycleFilter === "pending" ? "pd-pill-warn" : "pd-pill-neutral"}`}
                      style={{ cursor: "pointer" }}
                    >
                      Pending
                    </button>
                    <button
                      onClick={() => setCycleFilter("approved")}
                      className={`pd-pill ${cycleFilter === "approved" ? "pd-pill-ok" : "pd-pill-neutral"}`}
                      style={{ cursor: "pointer" }}
                    >
                      Approved
                    </button>
                  </div>
                </div>
                <div className="space-y-3 mt-2">
                  {Object.values(groupedClearances)
                    .sort((a, b) => (a.cycleNo || 0) - (b.cycleNo || 0))
                    .filter((group) => {
                      if (cycleFilter === "all") return true;
                      const pending = (group.items || []).some(
                        (c) =>
                          !c.approved &&
                          !(
                            c.approvedById ||
                            (c.approvedBy && c.approvedBy.id)
                          ),
                      );
                      if (cycleFilter === "pending") return pending;
                      if (cycleFilter === "approved") return !pending;
                      return true;
                    })
                    .map((group) => (
                      <div
                        key={`cycle-${group.cycleNo}`}
                        className="pd-card max-w-screen-lg mx-auto"
                      >
                        {(() => {
                          const pending = (group.items || []).some(
                            (c) =>
                              !c.approved &&
                              !(
                                c.approvedById ||
                                (c.approvedBy && c.approvedBy.id)
                              ),
                          );
                          return (
                            <div className="pd-card-head" style={{ background: pending ? "var(--pd-warn-wash)" : "var(--pd-ok-wash)" }}>
                              <h2 style={{ color: pending ? "var(--pd-warn)" : "var(--pd-ok)" }}>
                                {group.approvalType || "INITIAL"} — Cycle {group.cycleNo}
                              </h2>
                              <span className="text-xs font-medium" style={{ color: pending ? "var(--pd-warn)" : "var(--pd-ok)" }}>
                                {pending ? "Pending" : "All approved"}
                              </span>
                            </div>
                          );
                        })()}
                        <div className="pd-card-body grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                          {(group.items || []).map((c) => {
                            const isReviewed = !!(
                              c.approvedById ||
                              (c.approvedBy &&
                                (c.approvedBy.id || c.approvedById))
                            );
                            const statusLabel =
                              c.approved == null || c.approved == undefined
                                ? "Pending"
                                : c.approved
                                  ? "Approved"
                                  : "Rejected";
                            const statusPillClass =
                              statusLabel === "Approved"
                                ? "pd-pill-ok"
                                : statusLabel === "Rejected"
                                  ? "pd-pill-crit"
                                  : "pd-pill-neutral";
                            const stripeClass =
                              statusLabel === "Rejected"
                                ? "rejected"
                                : statusLabel === "Pending"
                                  ? "pending"
                                  : "";
                            const ap = c.approvedBy;
                            const reviewerName =
                              c.approvedByName ||
                              (ap
                                ? ap.username ||
                                ap.displayName ||
                                ap.fullName ||
                                ap.name ||
                                (ap.firstName || ap.lastName
                                  ? `${ap.firstName || ""} ${ap.lastName || ""}`.trim()
                                  : null)
                                : null) ||
                              c.approvedById ||
                              "-";
                            const canReviewNow =
                              permit &&
                              (permit.status === "CLEARANCE_DEPARTMENT_REVIEW" ||
                                permit.status === "EXTEND_UNDER_REVIEW") &&
                              !Object.values(
                                clearanceServerApproved || {},
                              ).every((v) => v === true) &&
                              !isReviewed &&
                              userCanApproveClearance(c);

                            return (
                              <div
                                key={c.id || c.departmentId}
                                className={`pd-dept-card ${stripeClass} flex flex-col gap-2`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="font-semibold text-sm text-gray-800">
                                    {c.departmentName || c.departmentId}
                                  </div>
                                  {!canReviewNow && (
                                    <span className={`pd-pill ${statusPillClass}`}>
                                      {statusLabel}
                                    </span>
                                  )}
                                </div>

                                {isReviewed && (
                                  <div className="text-xs text-gray-600">
                                    By:{" "}
                                    <span className="font-medium">{reviewerName}</span>
                                  </div>
                                )}

                                {canReviewNow ? (
                                  <div className="border-t pt-2 mt-1 text-xs text-gray-500 italic">
                                    Awaiting your review — remark &amp; acceptance are at the bottom of the page.
                                  </div>
                                ) : (
                                  c.approved != null && c.approved != undefined && (
                                    <div className="border-t pt-2 mt-1">
                                      <div className="text-xs font-semibold text-gray-600">
                                        Remark
                                      </div>
                                      <div className="text-sm whitespace-pre-wrap text-gray-800">
                                        {c.remark ? c.remark : "-"}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {showRemarkModal && (
            <div className="fixed inset-0 !bg-black/40 bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded p-6 w-full max-w-lg mx-4">
                <h3 className="text-lg font-semibold mb-3">Provide remark</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Please enter a remark for this action.
                </p>
                <textarea
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  className="w-full border rounded p-2 mb-3"
                  rows={4}
                />
                {(remarkAction === "suspend" || remarkAction === "extend") && (
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {remarkAction === "suspend"
                        ? "Suspend Until"
                        : "Extend Until"}
                    </label>
                    <input
                      type="datetime-local"
                      value={remarkDate}
                      onChange={(e) => setRemarkDate(e.target.value)}
                      className="w-full border rounded p-2"
                    />
                  </div>
                )}
                {remarkAction === "close" &&
                  (() => {
                    const blocks =
                      permitTypeBlocks &&
                        Array.isArray(permitTypeBlocks) &&
                        permitTypeBlocks.length > 0
                        ? permitTypeBlocks
                        : null;
                    const fallbackItems = checklistItems || [];
                    if (
                      (!blocks || blocks.length === 0) &&
                      fallbackItems.length === 0
                    )
                      return null;
                    return (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Checklist review
                        </label>
                        <div className="space-y-2 max-h-48 overflow-auto border rounded p-2">
                          {blocks
                            ? blocks.map((b) => (
                              <div
                                key={
                                  b.id || b.permitTypeId || b.permitTypeName
                                }
                                className="mb-3"
                              >
                                <div className="text-sm font-semibold mb-1">
                                  {b.permitTypeName ||
                                    (b.permitType && b.permitType.name) ||
                                    `Permit Type`}
                                </div>
                                {(b.checklistItems || []).map((c) => (
                                  <div
                                    key={
                                      c.id ||
                                      `${b.id}-${c.permitTypeChecklistId || Math.random()}`
                                    }
                                    className="flex items-center justify-between"
                                  >
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="checkbox"
                                        checked={!!checklistReview[c.id]}
                                        onChange={(e) =>
                                          setChecklistReview((prev) => ({
                                            ...prev,
                                            [c.id]: e.target.checked,
                                          }))
                                        }
                                      />
                                      <div>
                                        <div className="font-medium">
                                          {c.text}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                          {c.mandatory
                                            ? "Mandatory"
                                            : "Optional"}
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      className={`px-2 py-1 text-xs rounded ${c.status === "DONE" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}
                                    >
                                      {c.status || "PENDING"}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))
                            : fallbackItems.map((c) => (
                              <div
                                key={c.id}
                                className="flex items-center justify-between"
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={!!checklistReview[c.id]}
                                    onChange={(e) =>
                                      setChecklistReview((prev) => ({
                                        ...prev,
                                        [c.id]: e.target.checked,
                                      }))
                                    }
                                  />
                                  <div>
                                    <div className="font-medium">
                                      {c.text}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {c.mandatory ? "Mandatory" : "Optional"}
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className={`px-2 py-1 text-xs rounded ${c.status === "DONE" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}
                                >
                                  {c.status || "PENDING"}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  })()}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowRemarkModal(false)}
                    disabled={buttonsDisabled}
                    className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRemark}
                    disabled={buttonsDisabled}
                    className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {showAuditModal && (
            <div className="fixed inset-0 !bg-black/40 bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded p-2 w-full h-full sm:h-auto max-w-3xl overflow-hidden">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Audit Log</h3>
                  <button
                    onClick={() => setShowAuditModal(false)}
                    className="text-gray-600"
                  >
                    Close
                  </button>
                </div>

                <div
                  ref={auditContainerRef}
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    if (
                      !auditLoading &&
                      auditHasMore &&
                      el.scrollTop + el.clientHeight >= el.scrollHeight - 120
                    ) {
                      loadAuditLogs(auditPage + 1, true);
                    }
                  }}
                  className=""
                  style={{ maxHeight: "60vh", overflowY: "auto" }}
                >
                  {auditLogs.length === 0 && !auditLoading && (
                    <p className="text-sm text-gray-600">
                      No audit logs found.
                    </p>
                  )}

                  {auditLogs.length > 0 && (
                    <Timeline
                      items={auditLogs}
                      onView={(log) => {
                        setSelectedLog(log);
                        setShowLogModal(true);
                      }}
                    />
                  )}

                  {auditLoading && (
                    <div className="text-center py-3 text-sm text-gray-600">
                      Loading...
                    </div>
                  )}

                  {!auditLoading && auditHasMore && (
                    <div className="flex justify-center mt-3">
                      <button
                        onClick={() => loadAuditLogs(auditPage + 1, true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded"
                      >
                        Load more
                      </button>
                    </div>
                  )}
                </div>

                {showLogModal && selectedLog && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-60"
                    style={{ backgroundColor: "rgba(0,0,0,0.4) !important" }}
                  >
                    <div className="bg-white rounded p-2 w-full h-full sm:h-auto max-w-2xl overflow-auto">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">
                          Audit Log Details
                        </h3>
                        <button
                          onClick={() => setShowLogModal(false)}
                          className="text-gray-600"
                        >
                          Close
                        </button>
                      </div>
                      <div className="space-y-3 text-sm text-gray-800">
                        <div>
                          <strong>Action:</strong> {selectedLog.action}
                        </div>
                        <div>
                          <strong>Actor:</strong>{" "}
                          {selectedLog.actorId || "system"}
                        </div>
                        <div>
                          <strong>Timestamp:</strong>{" "}
                          {selectedLog.timestamp
                            ? selectedLog.timestamp.replace("T", " ").slice(0, 16)
                            : ""}
                        </div>
                        {selectedLog.remark && (
                          <div>
                            <strong>Remark:</strong> {selectedLog.remark}
                          </div>
                        )}
                        <div>
                          <strong>New Value:</strong>{" "}
                          <pre className="whitespace-pre-wrap">
                            {selectedLog.newValue || "-"}
                          </pre>
                        </div>
                        <div>
                          <strong>Old Value:</strong>{" "}
                          <pre className="whitespace-pre-wrap">
                            {selectedLog.oldValue || "-"}
                          </pre>
                        </div>
                      </div>
                      <div className="flex justify-end mt-4">
                        <button
                          onClick={() => setShowLogModal(false)}
                          className="px-4 py-2 bg-blue-600 text-white rounded"
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}


        </>
      )}

      {/* ─── Revalidation Overdue Warning Banner ─── */}
      {permit && ["ACTIVE", "EXTENDED"].includes(permit.status) && permit.revalidationRequired && (() => {
        const nextDue = getNextRevalidationDue();
        if (!nextDue) return null;
        const overdue = nextDue < new Date();
        if (!overdue) return null;
        return (
          <div className="max-w-screen-lg mx-auto mb-4 mt-6">
            <div className="bg-red-50 border-l-4 border-red-600 p-2 rounded flex items-start gap-3">
              <span className="text-red-600 text-xl font-bold">⚠</span>
              <div>
                <p className="font-semibold text-red-800 m-0">Shift Revalidation Overdue</p>
                <p className="text-red-700 text-sm m-0">
                  This permit requires revalidation every {permit.revalidationIntervalHours}h. It was due at{" "}
                  {toDatetimeLocalInput(nextDue).replace("T", " ")}. Record a fresh shift revalidation.
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ─── Permit Revalidation Grid ─── */}
      {permit && (
        <div className="pd-card max-w-screen-lg mx-auto mt-3">
          <div className="pd-card-head">
            <h2>Shift-wise Revalidation</h2>
            {["ACTIVE", "EXTENDED"].includes(permit.status) && user?.authorities?.includes(ptw_can_manage_revalidation) && (
              <button onClick={() => {
                setRevalShiftLabel("Morning");
                const nextDue = getNextRevalidationDue();
                setRevalValidUntil(nextDue ? toDatetimeLocalInput(nextDue) : "");
                setRevalIssuerRemark("");
                setShowRevalModal(true);
              }}
                disabled={buttonsDisabled}
                title="Record shift revalidation"
                className="pd-btn-add" style={{ background: "#7c3aed" }}>
                +
              </button>
            )}
          </div>
          <div className="pd-card-body">
            {permit.revalidationRequired && permit.revalidationIntervalHours && (
              <div className="text-xs text-gray-500 mb-2">
                Required every {permit.revalidationIntervalHours}h
                {(() => {
                  const nextDue = getNextRevalidationDue();
                  return nextDue ? (
                    <> • Next due: {toDatetimeLocalInput(nextDue).replace("T", " ")}</>
                  ) : null;
                })()}
              </div>
            )}
            {revalidations.length === 0 ? (
              <p className="text-sm text-gray-500">No revalidation records.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {revalidations.map(rev => (
                  <div
                    key={rev.id}
                    className={`pd-dept-card ${rev.receiverSignedAt ? "" : "pending"} flex flex-col gap-1.5`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-800">
                        Shift {rev.shiftNo} — {rev.shiftLabel || "—"}
                      </span>
                      <span className="text-xs text-gray-500">{rev.revalidationDate || "—"}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      <span className="font-semibold text-gray-600">Valid Until: </span>
                      {rev.validUntil ? rev.validUntil.replace("T", " ").slice(0, 16) : "—"}
                    </div>
                    <div className="text-xs border-t pt-1.5">
                      <span className="font-semibold text-gray-600">Issuer Signed: </span>
                      {rev.issuerSignedByName || "—"}
                      {rev.issuerSignedAt && <span className="block text-gray-400">{rev.issuerSignedAt.replace("T", " ").slice(0, 16)}</span>}
                      {rev.issuerRemark && <span className="block text-gray-500 italic">{rev.issuerRemark}</span>}
                    </div>
                    <div className="text-xs border-t pt-1.5">
                      <span className="font-semibold text-gray-600">Receiver Signed: </span>
                      {rev.receiverSignedAt
                        ? <>{rev.receiverSignedByName}<span className="block text-gray-400">{rev.receiverSignedAt.replace("T", " ").slice(0, 16)}</span>
                          {rev.receiverRemark && <span className="block text-gray-500 italic">{rev.receiverRemark}</span>}</>
                        : <span className="text-yellow-700 font-medium">Pending</span>}
                    </div>
                    {!rev.receiverSignedAt && user?.authorities?.includes(ptw_can_manage_revalidation) && (
                      <button onClick={() => { setReceiverSignId(rev.id); setReceiverSignRemark(""); setShowReceiverSignModal(true); }}
                        disabled={buttonsDisabled}
                        className="mt-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 self-start">
                        Receiver Sign
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Isolation / LOTO Records Section ─── */}
      {permit && (
        <div className="pd-card max-w-screen-lg mx-auto mt-3">
          <div className="pd-card-head">
            <h2>Isolation / LOTO Records</h2>
            {permit.status === "ACTIVE" && user?.authorities?.includes(ptw_can_manage_isolation) && (
              <button onClick={() => { setIsoPoint(""); setIsoMethod("LOCKOUT"); setIsoEquipTag(""); setIsoLockTag(""); setShowIsoModal(true); }}
                disabled={buttonsDisabled}
                title="Add isolation point"
                className="pd-btn-add" style={{ background: "#c2410c" }}>
                +
              </button>
            )}
          </div>
          <div className="pd-card-body">
            {isolationRecords.length === 0 ? (
              <p className="text-sm text-gray-500">No isolation records.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {isolationRecords.map((rec, i) => (
                  <div
                    key={i}
                    className={`pd-dept-card ${rec.status === "REINSTATED" ? "" : "rejected"} flex flex-col gap-1.5`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-800 break-words">
                        {rec.isolationPoint}
                      </span>
                      <span className={`pd-pill ${rec.status === "REINSTATED" ? "pd-pill-ok" : "pd-pill-crit"}`}>
                        {rec.status}
                      </span>
                    </div>
                    <div className="text-xs">
                      <span className="px-1.5 py-0.5 bg-orange-100 text-orange-800 rounded font-medium">{rec.isolationMethod}</span>
                      {rec.lockTagNumber && (
                        <span className="ml-2 text-gray-500">Lock/Tag #: {rec.lockTagNumber}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 border-t pt-1.5">
                      <span className="font-semibold text-gray-600">Isolated By: </span>
                      {rec.isolatedByName || "—"}
                      {rec.isolatedAt && (
                        <span className="block text-gray-400">{rec.isolatedAt.replace("T", " ").slice(0, 16)}</span>
                      )}
                    </div>
                    {rec.status === "ISOLATED" && !["CLOSED", "CANCELLED", "REJECTED"].includes(permit.status) &&
                      user?.authorities?.includes(ptw_can_manage_isolation) && (
                        <button onClick={() => { setReinstateId(rec.id); setReinstateRemark(""); setShowReinstateModal(true); }}
                          disabled={buttonsDisabled}
                          className="mt-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 self-start">
                          Reinstate
                        </button>
                      )}
                    {rec.status === "REINSTATED" && rec.reinstatementByName && (
                      <div className="text-xs text-gray-500 border-t pt-1.5">
                        Reinstated by {rec.reinstatementByName}
                        {rec.reinstatementRemark && <span className="block italic">{rec.reinstatementRemark}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* ─── Permit Display at Worksite ─── */}
      {/* {permit && ["ISSUED", "ACTIVE"].includes(permit.status) && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mt-6 mb-4 max-w-screen-lg mx-auto relative">
          <label className="absolute -top-3 left-3 bg-white px-2 text-sm font-semibold text-gray-700">
            Physical Permit Posted at Worksite
          </label>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              {permit.displayedAtWorksite ? (
                <p className="text-sm text-green-700 mt-1">
                  ✓ Confirmed by <strong>{permit.worksiteDisplayConfirmedBy}</strong> at{" "}
                  {permit.worksiteDisplayConfirmedAt?.replace("T", " ").slice(0, 16)}
                </p>
              ) : (
                <p className="text-sm text-amber-700 mt-1">
                  ⚠ Not yet confirmed — physical copy must be posted at the worksite.
                </p>
              )}
            </div>
            {!permit.displayedAtWorksite && (
              <button
                disabled={buttonsDisabled}
                onClick={() => {
                  setButtonsDisabledWithLog(true);
                  fetch(`${restApiUrl}/api/permits/${id}/confirm-worksite-display`, {
                    method: "POST", mode: "cors", headers: headers(),
                  })
                    .then(r => r.json())
                    .then(j => {
                      if (j && j.success) {
                        showSuccess("Permit display at worksite confirmed.");
                        setReloadCounter(c => c + 1);
                      } else { showError(j.message || "Failed to confirm display."); }
                    })
                    .catch(() => showError("Network error."))
                    .finally(() => setButtonsDisabledWithLog(false));
                }}
                className="bg-amber-600 text-white px-4 py-2 rounded text-sm hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Posted at Worksite
              </button>
            )}
          </div>
        </div>
      )} */}

      {/* ─── Blanket Permit ─── */}
      {permit && permit.permitScope === "BLANKET" && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl shadow-sm p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">BLANKET PERMIT</span>
            <h3 className="text-base font-semibold text-gray-800">Zone-Wide Coverage</h3>
          </div>

          {/* Daily check-in */}
          {permit.status === "ACTIVE" && user?.authorities?.includes(ptw_can_activate_permit) && (
            <div className="bg-white border border-amber-200 rounded-lg p-2 mb-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Daily Check-In Confirmation</p>
              <div className="flex gap-2 items-center">
                <input type="text" placeholder="Remarks (optional)"
                  value={blanketCheckinRemark}
                  onChange={(e) => setBlanketCheckinRemark(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm flex-1" />
                <button onClick={submitBlanketCheckin} disabled={buttonsDisabled}
                  className="bg-amber-600 text-white px-3 py-1 rounded text-sm hover:bg-amber-700 disabled:opacity-50">
                  Confirm Today's Check-In
                </button>
              </div>
            </div>
          )}

          {/* Check-in log */}
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Check-In Log</p>
          {blanketCheckins.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No check-ins recorded yet.</p>
          ) : (
            <div className="space-y-1">
              {blanketCheckins.map((ci) => (
                <div key={ci.id} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2 border border-amber-100">
                  <span className="font-medium text-gray-700">{ci.checkinDate}</span>
                  {ci.confirmedByName && <span className="text-gray-500 ml-3">{ci.confirmedByName}</span>}
                  {ci.confirmedAt && <span className="text-gray-400 text-xs ml-3">{ci.confirmedAt.replace("T", " ").slice(0, 16)}</span>}
                  {ci.remarks && <span className="text-gray-500 italic text-xs ml-3">{ci.remarks}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Master / Child Permit Hierarchy ─── */}
      {permit && (
        <div className="pd-card max-w-screen-lg mx-auto mt-3">
          <div className="pd-card-head">
            <h2>Permit Hierarchy</h2>
            {!permit.parentPermitId && !["CLOSED", "CANCELLED", "REJECTED"].includes(permit.status) && (
              <button
                onClick={() => setShowLinkForm(true)}
                title="Link a child permit"
                className="pd-btn-add" style={{ background: "#7c3aed" }}
              >
                +
              </button>
            )}
          </div>
          <div className="pd-card-body">

            {/* Parent permit */}
            {permit.parentPermitId && (
              <div className="mb-3 text-sm">
                <span className="text-gray-500">Part of master permit: </span>
                <a href={`../permits/${permit.parentPermitId}`}
                  className="text-blue-600 hover:underline font-medium">
                  #{permit.parentPermitId}
                  {permit.parentPermitSerialNumber ? ` (${permit.parentPermitSerialNumber})` : ""}
                </a>
              </div>
            )}

            {/* Child permits */}
            {permit.childPermits && permit.childPermits.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {permit.childPermits.map((c) => (
                  <div key={c.id} className="pd-mini-card flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <a href={`../permits/${c.id}`} className="text-blue-600 hover:underline font-medium text-sm">
                        #{c.id}{c.serialNumber ? ` — ${c.serialNumber}` : ""}
                      </a>
                      <span className="pd-pill pd-pill-info">{c.status}</span>
                    </div>
                    {c.permitTypes && <div className="text-xs text-gray-500">{c.permitTypes}</div>}
                    {!["CLOSED", "CANCELLED", "REJECTED"].includes(permit.status) && (
                      <button onClick={() => unlinkChild(c.id)}
                        className="text-red-500 hover:text-red-700 text-xs self-start">Unlink</button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              !permit.parentPermitId && <p className="text-sm text-gray-500">No child permits linked.</p>
            )}

            {/* Link new child */}
            {showLinkForm && (
              <div className="flex items-center gap-2 mt-3">
                <input type="number" placeholder="Child Permit ID"
                  value={linkChildId} onChange={(e) => setLinkChildId(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-36" />
                <button onClick={submitLinkChild} disabled={buttonsDisabled}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50">
                  Link
                </button>
                <button onClick={() => { setShowLinkForm(false); setLinkChildId(""); }}
                  className="text-gray-500 hover:text-gray-700 text-sm">Cancel</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── PTW Coordination Meeting Log ─── */}
      {permit && (
        <div className="pd-card max-w-screen-lg mx-auto mt-3">
          <div className="pd-card-head">
            <h2>Coordination Meeting Log</h2>
            {!["CLOSED", "CANCELLED", "REJECTED"].includes(permit.status) && user?.authorities?.includes(ptw_can_manage_meeting) && (
              <button
                onClick={() => setShowMeetingForm(true)}
                title="Add meeting"
                className="pd-btn-add" style={{ background: "var(--pd-info)" }}
              >
                +
              </button>
            )}
          </div>
          <div className="pd-card-body">

            {showMeetingForm && (
              <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                <div className="bg-white rounded-none sm:rounded-lg shadow-xl w-full h-full sm:h-auto sm:max-w-lg sm:max-h-[85vh] flex flex-col p-6 overflow-y-auto space-y-3">
                  <h3 className="text-lg font-semibold mb-1">Record Coordination Meeting</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Meeting Date & Time *</label>
                      <input type="datetime-local" value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                      <input type="text" value={meetingLocation} placeholder="e.g. Site Office, Conference Room"
                        onChange={(e) => setMeetingLocation(e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Agenda</label>
                    <textarea value={meetingAgenda} rows={2} placeholder="Topics to be discussed..."
                      onChange={(e) => setMeetingAgenda(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Attendees</label>
                    <div className="space-y-2 mb-2">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="text-xs text-gray-500 sm:w-32 shrink-0">Company employee</span>
                        <select
                          value={meetingSelectedEmployeeId}
                          onChange={(e) => setMeetingSelectedEmployeeId(e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm min-w-0"
                        >
                          <option value="">-- Select employee --</option>
                          {allEmployees.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.empCode ? `${emp.empCode} - ` : ""}{emp.employeeName}
                            </option>
                          ))}
                        </select>
                        <button type="button" onClick={addMeetingEmployeeAttendee}
                          className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 whitespace-nowrap">
                          + Add Employee
                        </button>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="text-xs text-gray-500 sm:w-32 shrink-0">Contract person</span>
                        <input type="text" value={meetingContractPersonName}
                          placeholder="Name"
                          onChange={(e) => setMeetingContractPersonName(e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm min-w-0" />
                        <input type="text" value={meetingContractPersonAgency}
                          placeholder="Agency (optional)"
                          onChange={(e) => setMeetingContractPersonAgency(e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm min-w-0" />
                        <button type="button" onClick={addMeetingContractPersonAttendee}
                          className="bg-amber-600 text-white px-3 py-1.5 rounded text-sm hover:bg-amber-700 whitespace-nowrap">
                          + Add Contractor
                        </button>
                      </div>
                    </div>
                    {meetingAttendeeList.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {meetingAttendeeList.map((a) => (
                          <span key={a.label}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${a.contractorName ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                            {a.label} {a.contractorName ? "(Contractor)" : ""}
                            <button type="button" onClick={() => removeMeetingAttendee(a.label)}
                              className="ml-1 text-gray-500 hover:text-red-600">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Minutes / Outcomes</label>
                    <textarea value={meetingMinutes} rows={3} placeholder="Key decisions, action items..."
                      onChange={(e) => setMeetingMinutes(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setShowMeetingForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded text-sm" disabled={buttonsDisabled}>
                      Cancel
                    </button>
                    <button onClick={submitCoordinationMeeting}
                      className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                      disabled={buttonsDisabled}>
                      {buttonsDisabled ? "Saving..." : "Save Meeting Record"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {coordinationMeetings.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No coordination meetings recorded yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {[...coordinationMeetings].sort((a, b) => new Date(a.meetingDate || 0) - new Date(b.meetingDate || 0)).map((m) => (
                  <div key={m.id} className="pd-mini-card flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-800">
                        {m.location || "—"}
                      </span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {m.meetingDate ? m.meetingDate.replace("T", " ").slice(0, 16) : "—"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      <span className="font-semibold text-gray-600">Conducted By: </span>
                      {m.conductedByName || "—"}
                    </div>
                    <div className="text-xs">
                      <span className="font-semibold text-gray-600">Attendees: </span>
                      {m.attendees && m.attendees.length > 0
                        ? m.attendees.map((a) =>
                          a.employeeName ? a.employeeName : `${a.contractorName}${a.contractorAgency ? ` (${a.contractorAgency})` : ""} [Contractor]`
                        ).join(", ")
                        : "—"}
                    </div>
                    <div className="text-xs border-t pt-1.5">
                      <span className="font-semibold text-gray-600">Agenda: </span>
                      {m.agenda || "—"}
                    </div>
                    <div className="text-xs">
                      <span className="font-semibold text-gray-600">Minutes: </span>
                      {m.minutes || "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Gas Tests Section ─── */}
      {permit && (
        <div className="pd-card max-w-screen-lg mx-auto mt-3">
          <div className="pd-card-head">
            <h2>Gas Tests</h2>
            {["ACTIVE", "ISSUED"].includes(permit.status) && user?.authorities?.includes(ptw_can_record_gas_test) && (
              <button onClick={() => {
                setGasTestEntries([{ parameter: gasTestParamOptions[0]?.name || "", reading: "" }]);
                setShowGasTestModal(true);
              }} disabled={buttonsDisabled}
                title="Record gas test"
                className="pd-btn-add" style={{ background: "var(--pd-crit)" }}>
                +
              </button>
            )}
          </div>
          <div className="pd-card-body">
            {gasTests.length === 0 ? (
              <p className="text-sm text-gray-500">No gas tests recorded.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {[...gasTests].sort((a, b) => new Date(a.testedAt || 0) - new Date(b.testedAt || 0)).map((g, i) => {
                  const expired = g.validUntil && new Date(g.validUntil) < new Date();
                  return (
                    <div
                      key={i}
                      className={`pd-mini-card flex flex-col gap-1.5`}
                      style={expired ? { borderColor: "var(--pd-crit)" } : undefined}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`pd-pill ${g.category === "PRE_PERMIT" ? "pd-pill-info" :
                          g.category === "POST_SUSPENSION" ? "pd-pill-warn" :
                            "pd-pill-ok"
                          }`}>
                          {g.category ? g.category.replace("_", " ") : "PERIODIC"}
                        </span>
                        {g.validUntil ? (
                          expired
                            ? <span className="pd-pill pd-pill-crit">Expired</span>
                            : <span className="pd-pill pd-pill-ok">Valid</span>
                        ) : null}
                      </div>
                      <div className="text-sm pd-mono">
                        {(g.readings || []).length > 0
                          ? (g.readings || []).map((r, ri) => (
                            <div key={ri}>
                              <span className="font-medium">{r.parameter}</span>: {r.reading}
                            </div>
                          ))
                          : "—"}
                      </div>
                      <div className="text-xs text-gray-500 border-t pt-1.5">
                        <span className="font-semibold text-gray-600">Tested At: </span>
                        {g.testedAt ? g.testedAt.replace("T", " ").slice(0, 16) : "—"}
                      </div>
                      <div className="text-xs text-gray-500">
                        <span className="font-semibold text-gray-600">Valid Until: </span>
                        {g.validUntil ? g.validUntil.replace("T", " ").slice(0, 16) : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gas Test Modal */}
      {showGasTestModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-none sm:rounded-lg shadow-xl w-full h-full sm:h-auto sm:max-w-md sm:max-h-[85vh] flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 p-2 sm:p-6 sm:pb-0 pb-0 flex-shrink-0">Record Gas Test</h3>
            <div className="space-y-3 overflow-y-auto flex-1 px-4 sm:px-6 py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parameters &amp; Readings <span className="text-red-500">*</span></label>
                <div className="space-y-2">
                  {gasTestEntries.map((entry, idx) => {
                    const selectedElsewhere = new Set(
                      gasTestEntries.filter((_, i) => i !== idx).map((e) => e.parameter).filter(Boolean)
                    );
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <select value={entry.parameter} onChange={(e) => updateGasTestEntry(idx, "parameter", e.target.value)}
                          className="w-1/2 p-2 border border-gray-300 rounded text-sm">
                          <option value="">Select Parameter</option>
                          {gasTestParamOptions
                            .filter((p) => p.name === entry.parameter || !selectedElsewhere.has(p.name))
                            .map((p) => (
                              <option key={p.id} value={p.name}>{p.name}{p.unit ? ` (${p.unit})` : ""}</option>
                            ))}
                        </select>
                        <input type="text" value={entry.reading} onChange={(e) => updateGasTestEntry(idx, "reading", e.target.value)}
                          placeholder="Reading" className="w-1/2 p-2 border border-gray-300 rounded text-sm" />
                        {gasTestEntries.length > 1 && (
                          <button type="button" onClick={() => removeGasTestEntry(idx)}
                            className="text-red-600 text-sm px-1" title="Remove row">✕</button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button type="button" onClick={addGasTestEntry}
                  className="mt-2 text-sm text-blue-600 hover:underline">+ Add another parameter</button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={gasTestCategory} onChange={(e) => setGasTestCategory(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm">
                  <option value="PRE_PERMIT">Pre-Permit</option>
                  <option value="PERIODIC">Periodic</option>
                  <option value="POST_SUSPENSION">Post-Suspension</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                <input type="datetime-local" value={gasTestValidUntil} onChange={(e) => setGasTestValidUntil(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tester <span className="text-red-500">*</span></label>
                <select value={gasTestTesterId} onChange={(e) => setGasTestTesterId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm">
                  <option value="">Select Tester</option>
                  {allEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.employeeName}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-2 sm:p-6 sm:pt-4 pt-3 border-t flex-shrink-0">
              <button onClick={() => { setShowGasTestModal(false); setGasTestEntries([{ parameter: gasTestParamOptions[0]?.name || "", reading: "" }]); setGasTestTesterId(""); }}
                className="px-4 py-2 border border-gray-300 rounded text-sm" disabled={buttonsDisabled}>Cancel</button>
              <button onClick={submitGasTest} disabled={buttonsDisabled}
                className="px-4 py-2 bg-red-600 text-white rounded text-sm disabled:opacity-50">
                {buttonsDisabled ? "Saving..." : "Save Gas Test"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Tool Box Talk Section ─── */}
      {permit && (
        <div className="pd-card max-w-screen-lg mx-auto mt-3">
          <div className="pd-card-head">
            <h2>Tool Box Talks (TBT)</h2>
            {["ACTIVE", "ISSUED"].includes(permit.status) && user?.authorities?.includes(ptw_can_manage_tbt) && (
              <button
                onClick={() => setShowTbtModal(true)}
                disabled={buttonsDisabled}
                title="Record TBT"
                className="pd-btn-add" style={{ background: "#15803d" }}
              >
                +
              </button>
            )}
          </div>
          <div className="pd-card-body">

            {toolBoxTalks.length === 0 ? (
              <p className="text-sm text-gray-500">No Tool Box Talks recorded yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {[...toolBoxTalks].sort((a, b) => new Date(a.conductedAt || a.createdAt || 0) - new Date(b.conductedAt || b.createdAt || 0)).map((t) => (
                  <div key={t.id} className="pd-mini-card flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-800 break-words">{t.topic}</span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {t.conductedAt ? t.conductedAt.replace("T", " ").slice(0, 16) : "—"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      <span className="font-semibold text-gray-600">Conducted By: </span>
                      {t.conductedByName || "—"}
                    </div>
                    <div className="text-xs">
                      <span className="font-semibold text-gray-600">Attendees: </span>
                      {Array.isArray(t.attendees) && t.attendees.length > 0
                        ? t.attendees.map((a) =>
                          a.employeeName ? a.employeeName : `${a.contractorName}${a.contractorAgency ? ` (${a.contractorAgency})` : ""} [Contractor]`
                        ).join(", ")
                        : typeof t.attendees === "string" && t.attendees
                          ? t.attendees
                          : "—"}
                    </div>
                    <div className="text-xs border-t pt-1.5">
                      <span className="font-semibold text-gray-600">Notes: </span>
                      {t.notes || "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* TBT Modal */}
            {showTbtModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2"
                style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                <div className="bg-white rounded-none sm:rounded-lg shadow-xl w-full h-full sm:h-auto sm:max-w-lg sm:max-h-[85vh] flex flex-col p-6 overflow-y-auto">
                  <h3 className="text-lg font-semibold mb-4">Record Tool Box Talk</h3>

                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Topic <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={tbtTopic}
                      onChange={(e) => setTbtTopic(e.target.value)}
                      placeholder="e.g. PPE inspection, emergency exits, hazard briefing..."
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Attendees
                    </label>
                    <div className="space-y-2 mb-2">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="text-xs text-gray-500 sm:w-32 shrink-0">Company employee</span>
                        <select
                          value={tbtSelectedEmployeeId}
                          onChange={(e) => setTbtSelectedEmployeeId(e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm min-w-0"
                        >
                          <option value="">-- Select employee --</option>
                          {allEmployees.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.empCode ? `${emp.empCode} - ` : ""}{emp.employeeName}
                            </option>
                          ))}
                        </select>
                        <button type="button" onClick={addTbtEmployeeAttendee}
                          className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 whitespace-nowrap">
                          + Add Employee
                        </button>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="text-xs text-gray-500 sm:w-32 shrink-0">Contract person</span>
                        <input type="text" value={tbtContractPersonName}
                          placeholder="Name"
                          onChange={(e) => setTbtContractPersonName(e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm min-w-0" />
                        <input type="text" value={tbtContractPersonAgency}
                          placeholder="Agency (optional)"
                          onChange={(e) => setTbtContractPersonAgency(e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm min-w-0" />
                        <button type="button" onClick={addTbtContractPersonAttendee}
                          className="bg-amber-600 text-white px-3 py-1.5 rounded text-sm hover:bg-amber-700 whitespace-nowrap">
                          + Add Contractor
                        </button>
                      </div>
                    </div>
                    {tbtAttendeeList.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {tbtAttendeeList.map((a) => (
                          <span key={a.label}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${a.contractorName ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                            {a.label} {a.contractorName ? "(Contractor)" : ""}
                            <button type="button" onClick={() => removeTbtAttendee(a.label)}
                              className="ml-1 text-gray-500 hover:text-red-600">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={tbtNotes}
                      onChange={(e) => setTbtNotes(e.target.value)}
                      rows={3}
                      placeholder="Key points discussed, hazards highlighted..."
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setShowTbtModal(false); setTbtTopic(""); setTbtNotes("");
                        setTbtAttendeeList([]); setTbtSelectedEmployeeId("");
                        setTbtContractPersonName(""); setTbtContractPersonAgency("");
                      }}
                      className="px-4 py-2 border border-gray-300 rounded text-sm"
                      disabled={buttonsDisabled}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitTbt}
                      disabled={buttonsDisabled}
                      className="px-4 py-2 bg-green-600 text-white rounded text-sm disabled:opacity-50"
                    >
                      {buttonsDisabled ? "Saving..." : "Save TBT"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Shift Handover Section ─── */}
      {permit && (
        <div className="pd-card max-w-screen-lg mx-auto mt-3">
          <div className="pd-card-head">
            <h2>Shift Handovers</h2>
            {permit.status === "ACTIVE" && (
              <button
                onClick={() => setShowHandoverModal(true)}
                disabled={buttonsDisabled}
                title="Record handover"
                className="pd-btn-add" style={{ background: "#1d4ed8" }}
              >
                +
              </button>
            )}
          </div>
          <div className="pd-card-body">

            {shiftHandovers.length === 0 ? (
              <p className="text-sm text-gray-500">No shift handovers recorded yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {shiftHandovers.map((h) => {
                  const isPending = h.accepted === null || h.accepted === undefined;
                  const canRespond = isPending && user && user.username === h.incomingUserName;
                  return (
                    <div key={h.id} className="pd-mini-card flex flex-col gap-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-gray-500">
                          {h.handoverTime ? h.handoverTime.replace("T", " ").slice(0, 16) : "—"}
                        </div>
                        <span className={`pd-pill ${isPending ? "pd-pill-warn" : h.accepted ? "pd-pill-ok" : "pd-pill-crit"}`}>
                          {isPending ? "Pending" : h.accepted ? "Accepted" : "Rejected"}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="font-semibold text-gray-600">Outgoing: </span>
                        {h.outgoingUserName || "—"}
                      </div>
                      <div className="text-sm">
                        <span className="font-semibold text-gray-600">Incoming: </span>
                        {h.incomingUserName || "—"}
                      </div>
                      <div className="text-xs border-t pt-1.5">
                        <span className="font-semibold text-gray-600">Remarks: </span>
                        {h.remarks || "—"}
                      </div>
                      {!isPending && h.acceptedRemark && (
                        <div className="text-xs">
                          <span className="font-semibold text-gray-600">Acceptance Remark: </span>
                          {h.acceptedRemark}
                        </div>
                      )}
                      {canRespond && (
                        <div className="border-t pt-1.5 flex flex-col gap-1.5">
                          {respondingHandover && respondingHandover.id === h.id ? (
                            <>
                              <textarea
                                value={handoverRejectRemark}
                                onChange={(e) => setHandoverRejectRemark(e.target.value)}
                                placeholder={
                                  respondingHandover.accepted
                                    ? "Acceptance remark (optional)..."
                                    : "Reason for rejecting (required)..."
                                }
                                className="w-full border rounded p-1 text-xs"
                                rows={2}
                              />
                              <div className="flex gap-1.5">
                                <button
                                  disabled={buttonsDisabled}
                                  onClick={async () => {
                                    await respondToHandover(h.id, respondingHandover.accepted, handoverRejectRemark);
                                    setRespondingHandover(null);
                                    setHandoverRejectRemark("");
                                  }}
                                  className={`flex-1 text-xs px-2 py-1 rounded text-white disabled:opacity-50 ${respondingHandover.accepted ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                                    }`}
                                >
                                  {respondingHandover.accepted ? "Confirm Accept" : "Confirm Reject"}
                                </button>
                                <button
                                  onClick={() => { setRespondingHandover(null); setHandoverRejectRemark(""); }}
                                  className="flex-1 text-xs px-2 py-1 rounded bg-gray-300 text-gray-800 hover:bg-gray-400"
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <div className="flex gap-1.5">
                              <button
                                disabled={buttonsDisabled}
                                onClick={() => setRespondingHandover({ id: h.id, accepted: true })}
                                className="flex-1 text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                              >
                                Accept
                              </button>
                              <button
                                disabled={buttonsDisabled}
                                onClick={() => setRespondingHandover({ id: h.id, accepted: false })}
                                className="flex-1 text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Revalidation Modal */}
            {showRevalModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                <div className="bg-white rounded-none sm:rounded-lg shadow-xl p-6 w-full h-full sm:h-auto sm:max-w-md sm:max-h-[85vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Record Shift Revalidation</h3>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
                    <select value={revalShiftLabel} onChange={e => setRevalShiftLabel(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm">
                      <option>Morning</option>
                      <option>Afternoon</option>
                      <option>Night</option>
                      <option>General</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                    <input type="datetime-local" value={revalValidUntil} onChange={e => setRevalValidUntil(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issuer Remark</label>
                    <textarea value={revalIssuerRemark} onChange={e => setRevalIssuerRemark(e.target.value)} rows={2} className="w-full p-2 border border-gray-300 rounded text-sm" placeholder="Conditions verified for shift continuation..." />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowRevalModal(false)} className="px-4 py-2 border border-gray-300 rounded text-sm" disabled={buttonsDisabled}>Cancel</button>
                    <button onClick={submitRevalidation} disabled={buttonsDisabled} className="px-4 py-2 bg-purple-600 text-white rounded text-sm disabled:opacity-50">
                      {buttonsDisabled ? "Saving..." : "Record"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Receiver Sign Modal */}
            {showReceiverSignModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                <div className="bg-white rounded-none sm:rounded-lg shadow-xl p-6 w-full h-full sm:h-auto sm:max-w-md sm:max-h-[85vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Receiver Counter-Sign</h3>
                  <p className="text-sm text-gray-600 mb-3">Confirm that work conditions are safe to continue for this shift.</p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remark</label>
                    <textarea value={receiverSignRemark} onChange={e => setReceiverSignRemark(e.target.value)} rows={2} className="w-full p-2 border border-gray-300 rounded text-sm" placeholder="Work site conditions verified..." />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setShowReceiverSignModal(false); setReceiverSignRemark(""); }} className="px-4 py-2 border border-gray-300 rounded text-sm" disabled={buttonsDisabled}>Cancel</button>
                    <button onClick={submitReceiverSign} disabled={buttonsDisabled} className="px-4 py-2 bg-purple-600 text-white rounded text-sm disabled:opacity-50">
                      {buttonsDisabled ? "Saving..." : "Sign"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Isolation Record Modal */}
            {showIsoModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                <div className="bg-white rounded-none sm:rounded-lg shadow-xl p-6 w-full h-full sm:h-auto sm:max-w-md sm:max-h-[85vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Add Isolation / LOTO Record</h3>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Isolation Point <span className="text-red-500">*</span></label>
                    <input value={isoPoint} onChange={e => setIsoPoint(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm" placeholder="e.g. Main circuit breaker CB-01" />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Isolation Method</label>
                    <select value={isoMethod} onChange={e => setIsoMethod(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm">
                      <option value="LOCKOUT">Lockout</option>
                      <option value="TAGOUT">Tagout</option>
                      <option value="BLANKING">Blanking</option>
                      <option value="ELECTRICAL_ISOLATION">Electrical Isolation</option>
                      <option value="VALVE_CLOSURE">Valve Closure</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Tag</label>
                    <input value={isoEquipTag} onChange={e => setIsoEquipTag(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm" placeholder="e.g. PUMP-101" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lock / Tag Number</label>
                    <input value={isoLockTag} onChange={e => setIsoLockTag(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm" placeholder="e.g. LK-2024-001" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowIsoModal(false)} className="px-4 py-2 border border-gray-300 rounded text-sm" disabled={buttonsDisabled}>Cancel</button>
                    <button onClick={submitIsolation} disabled={buttonsDisabled} className="px-4 py-2 bg-orange-600 text-white rounded text-sm disabled:opacity-50">
                      {buttonsDisabled ? "Saving..." : "Add Record"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Reinstatement Modal */}
            {showReinstateModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                <div className="bg-white rounded-none sm:rounded-lg shadow-xl p-6 w-full h-full sm:h-auto sm:max-w-md sm:max-h-[85vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirm Reinstatement</h3>
                  <p className="text-sm text-gray-600 mb-3">Confirm that energy has been restored and isolation has been removed safely.</p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reinstatement Remark</label>
                    <textarea value={reinstateRemark} onChange={e => setReinstateRemark(e.target.value)} rows={3} className="w-full p-2 border border-gray-300 rounded text-sm" placeholder="Describe reinstatement steps taken..." />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setShowReinstateModal(false); setReinstateRemark(""); }} className="px-4 py-2 border border-gray-300 rounded text-sm" disabled={buttonsDisabled}>Cancel</button>
                    <button onClick={submitReinstate} disabled={buttonsDisabled} className="px-4 py-2 bg-green-600 text-white rounded text-sm disabled:opacity-50">
                      {buttonsDisabled ? "Saving..." : "Confirm Reinstatement"}
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/* Resumption Sign-off Modal */}
            {showResumptionModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                <div className="bg-white rounded-none sm:rounded-lg shadow-xl p-6 w-full h-full sm:h-auto sm:max-w-md sm:max-h-[85vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Re-activate Permit — Resumption Sign-off</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Provide your sign-off remark confirming it is safe to resume work.
                    {permit && permit.permitTypeBlocks && permit.permitTypeBlocks.some(b => b.permitTypeCode === "CSE") && (
                      <span className="block mt-1 text-yellow-700 font-medium">
                        ⚠ Confined Space: a fresh gas test must have been recorded after the suspension.
                      </span>
                    )}
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Resumption Sign-off Remark <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={resumptionRemark}
                      onChange={(e) => setResumptionRemark(e.target.value)}
                      rows={3}
                      placeholder="Confirm conditions are safe to resume work (required)..."
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => { setShowResumptionModal(false); setResumptionRemark(""); }}
                      className="px-4 py-2 border border-gray-300 rounded text-sm"
                      disabled={buttonsDisabled}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitResumption}
                      disabled={buttonsDisabled || !resumptionRemark.trim()}
                      className="px-4 py-2 bg-yellow-600 text-white rounded text-sm disabled:opacity-50"
                    >
                      {buttonsDisabled ? "Saving..." : "Confirm & Re-activate"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Box-Up Clearance Modal */}
            {showBoxUpModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                <div className="bg-white rounded-none sm:rounded-lg shadow-xl p-6 w-full h-full sm:h-auto sm:max-w-md sm:max-h-[85vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Record Box-Up Clearance</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Certify that all workers and tools have been removed from the confined space and it is safe to close the permit.
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Clearance Remark <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={boxUpRemark}
                      onChange={(e) => setBoxUpRemark(e.target.value)}
                      rows={3}
                      placeholder="Confirm all workers and tools have exited the confined space..."
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => { setShowBoxUpModal(false); setBoxUpRemark(""); }}
                      className="px-4 py-2 border border-gray-300 rounded text-sm"
                      disabled={buttonsDisabled}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitBoxUpClearance}
                      disabled={buttonsDisabled || !boxUpRemark.trim()}
                      className="px-4 py-2 bg-teal-600 text-white rounded text-sm disabled:opacity-50"
                    >
                      {buttonsDisabled ? "Saving..." : "Confirm Box-Up Clearance"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Handover Modal */}
            {showHandoverModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-2"
                style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                <div className="bg-white rounded-none sm:rounded-lg shadow-xl p-6 w-full h-full sm:h-auto sm:max-w-md sm:max-h-[85vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold mb-4">Record Shift Handover</h3>

                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Incoming Shift-in-Charge <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={handoverIncomingUserId}
                      onChange={(e) => setHandoverIncomingUserId(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="">-- Select user --</option>
                      {allUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.username}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Remarks
                    </label>
                    <textarea
                      value={handoverRemarks}
                      onChange={(e) => setHandoverRemarks(e.target.value)}
                      rows={3}
                      placeholder="Handover notes, work status, pending items..."
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => { setShowHandoverModal(false); setHandoverIncomingUserId(""); setHandoverRemarks(""); }}
                      className="px-4 py-2 border border-gray-300 rounded text-sm"
                      disabled={buttonsDisabled}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitHandover}
                      disabled={buttonsDisabled}
                      className="px-4 py-2 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
                    >
                      {buttonsDisabled ? "Saving..." : "Save Handover"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      )}

      {/* Competent Person approval — shown last, right before Submit Competent Approval */}
      {permit && permit.status === "COMPETENT_PERSON_APPROVAL" &&
        permitTypeBlocks && permitTypeBlocks.length > 0 &&
        user &&
        [ptw_can_review_competent_person].some((authority) =>
          user.authorities === undefined ? false : user.authorities.includes(authority),
        ) &&
        (() => {
          const confinedBlocks = permitTypeBlocks
            .map((b, idx) => ({ b, idx }))
            .filter(
              ({ b }) =>
                b.permitTypeName && b.permitTypeName.toLowerCase().includes("confined"),
            );
          if (confinedBlocks.length === 0) return null;
          return (
            <div className="max-w-screen-lg mx-auto mt-3 flex flex-col gap-3">
              {confinedBlocks.map(({ b, idx }) => (
                <div key={b.id || idx} className="pd-card">
                  <div className="pd-card-head" style={{ justifyContent: "flex-start", gap: "10px", flexWrap: "wrap" }}>
                    <span
                      className="pd-pill"
                      style={{ background: b.permitTypeColor || "#6b7280", color: "#fff" }}
                    >
                      {b.permitTypeName || `Type ${idx + 1}`}
                    </span>
                    <span className="text-xs text-gray-500">Competent Person</span>
                  </div>
                  <div className="pd-card-body">
                    <input
                      type="text"
                      value={b.competentPersonName || ""}
                      onChange={(e) => {
                        const next = [...permitTypeBlocks];
                        next[idx] = {
                          ...next[idx],
                          competentPersonName: e.target.value,
                        };
                        setPermitTypeBlocks(next);
                        if (next[idx] && next[idx].id) {
                          setEditedBlockIds((prev) =>
                            prev.includes(next[idx].id) ? prev : [...prev, next[idx].id],
                          );
                        }
                      }}
                      className="w-full border rounded p-2 mb-2"
                      placeholder="Competent Person Name"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-sm mr-1 font-bold">
                        Competent Person Acceptance :
                      </span>
                      <select
                        value={
                          b.competentPersonApproved === null || b.competentPersonApproved === undefined
                            ? ""
                            : b.competentPersonApproved ? "yes" : "no"
                        }
                        onChange={(e) => {
                          const val = e.target.value === "yes";
                          const next = [...permitTypeBlocks];
                          next[idx] = {
                            ...next[idx],
                            competentPersonApproved: val,
                          };
                          setPermitTypeBlocks(next);
                          if (next[idx] && next[idx].id) {
                            setEditedBlockIds((prev) =>
                              prev.includes(next[idx].id) ? prev : [...prev, next[idx].id],
                            );
                          }
                        }}
                        className="border rounded p-1 text-sm bg-white"
                      >
                        <option value="">Select</option>
                        <option value="yes">Approve</option>
                        <option value="no">Reject</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

      {/* Clearance Department reviewer decisions — shown last, right before Submit Clearance Review.
          Same cycle-grouped card layout as "Clearance — by Cycle" above, but only the cycles/departments
          this user can still act on, with the editable Remark + Acceptance form. */}
      {permit &&
        (permit.status === "CLEARANCE_DEPARTMENT_REVIEW" || permit.status === "EXTEND_UNDER_REVIEW") &&
        Array.isArray(permit.clearanceDepartments) &&
        (() => {
          const isPendingForMe = (c) => {
            const isReviewed = !!(
              c.approvedById ||
              (c.approvedBy && (c.approvedBy.id || c.approvedById))
            );
            return (
              !Object.values(clearanceServerApproved || {}).every((v) => v === true) &&
              !isReviewed &&
              userCanApproveClearance(c)
            );
          };
          const groups = Object.values(groupedClearances)
            .sort((a, b) => (a.cycleNo || 0) - (b.cycleNo || 0))
            .map((group) => ({
              ...group,
              items: (group.items || []).filter(isPendingForMe),
            }))
            .filter((group) => group.items.length > 0);
          if (groups.length === 0) return null;
          return (
            <div className="space-y-3 mt-3">
              {groups.map((group) => (
                <div key={`cycle-review-${group.cycleNo}`} className="pd-card max-w-screen-lg mx-auto">
                  <div className="pd-card-head" style={{ background: "var(--pd-warn-wash)" }}>
                    <h2 style={{ color: "var(--pd-warn)" }}>
                      {group.approvalType || "INITIAL"} — Cycle {group.cycleNo}
                    </h2>
                    <span className="text-xs font-medium" style={{ color: "var(--pd-warn)" }}>
                      Your review
                    </span>
                  </div>
                  <div className="pd-card-body grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {group.items.map((c) => (
                      <div key={c.id || c.departmentId} className="pd-dept-card pending flex flex-col gap-2">
                        <div className="font-semibold text-sm text-gray-800">
                          {c.departmentName || c.departmentId}
                        </div>
                        <div className="border-t pt-2 mt-1">
                          <div className="text-xs font-semibold text-gray-600">
                            Remark{" "}
                            {!clearanceEdits[c.id] && (
                              <span className="text-red-600 font-semibold">(required)</span>
                            )}
                            {clearanceEdits[c.id] && (
                              <span className="font-normal text-gray-400">(optional)</span>
                            )}
                          </div>
                          <textarea
                            value={clearanceRemarks[c.id] || ""}
                            id={`clearance-remark-${c.id}`}
                            onChange={(e) => {
                              const v = e.target.value;
                              setClearanceRemarks((prev) => ({
                                ...prev,
                                [c.id]: v,
                              }));
                              setClearanceValidationErrors((prev) => ({
                                ...prev,
                                [c.id]: false,
                              }));
                            }}
                            className="w-full border rounded p-1 mt-1 text-sm"
                            rows={2}
                            required={!clearanceEdits[c.id]}
                          />
                          {clearanceValidationErrors[c.id] && (
                            <div className="text-xs text-red-600 mt-1">
                              Remark is required when selecting "No".
                            </div>
                          )}

                          <label className="text-xs text-gray-600 font-medium block mt-2">
                            Acceptance
                          </label>
                          <select
                            value={
                              clearanceEdits[c.id] === null || clearanceEdits[c.id] === undefined
                                ? ""
                                : clearanceEdits[c.id]
                                  ? "yes"
                                  : "no"
                            }
                            onChange={(e) => {
                              const val = e.target.value === "yes";
                              setClearanceEdits((prev) => ({
                                ...prev,
                                [c.id]: val,
                              }));
                            }}
                            className="w-full border rounded p-1 bg-white text-sm mt-1"
                          >
                            <option value="">Select</option>
                            <option value="yes">Approve</option>
                            <option value="no">Reject</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

      {/* Permit Type reviewer decisions — shown last, right before Submit Review.
          Only for blocks this user can actually review right now (pending, authorized,
          role matches, and clearances already approved) — not every block on the permit. */}
      {permit && permit.status === "PERMIT_TYPE_REVIEW" &&
        permitTypeBlocks && permitTypeBlocks.length > 0 &&
        (() => {
          const reviewableBlocks = permitTypeBlocks.filter(
            (b) =>
              !b.approvedById &&
              user &&
              [
                ptw_can_approve_hot_work,
                ptw_can_approve_cold_work,
                ptw_can_approve_electrical_work,
                ptw_can_approve_confined_space_entry,
                ptw_can_approve_work_at_height,
                ptw_can_approve_excavation_work,
                ptw_can_approve_chemical_handling,
              ].some((authority) => user.authorities?.includes(authority)) &&
              isBlockClearancesApproved(b) &&
              userHasRequiredRole(b),
          );
          if (reviewableBlocks.length === 0) return null;
          return (
            <div className="max-w-screen-lg mx-auto mt-3 flex flex-col gap-3">
              {reviewableBlocks.map((b, idx) => (
                <div key={b.id || idx} className="pd-card">
                  <div className="pd-card-head" style={{ justifyContent: "flex-start", gap: "10px", flexWrap: "wrap" }}>
                    <span
                      className="pd-pill"
                      style={{ background: b.permitTypeColor || "#6b7280", color: "#fff" }}
                    >
                      {b.permitTypeName || `Type ${idx + 1}`}
                    </span>
                    <span className="text-xs text-gray-500">Reviewer decision</span>
                  </div>
                  <div className="pd-card-body">
                    <div className="flex flex-col sm:flex-row gap-3 items-start w-full">
                      <div className="flex-1 w-full">
                        <div className="pd-field-label mb-1">Reviewer Remark</div>
                        {!b.approvedById &&
                          user &&
                          [
                            ptw_can_approve_hot_work,
                            ptw_can_approve_cold_work,
                            ptw_can_approve_electrical_work,
                            ptw_can_approve_confined_space_entry,
                            ptw_can_approve_work_at_height,
                            ptw_can_approve_excavation_work,
                            ptw_can_approve_chemical_handling,
                          ].some((authority) =>
                            user.authorities?.includes(authority),
                          ) ? (
                          isBlockClearancesApproved(b) &&
                            userHasRequiredRole(b) ? (
                            <textarea
                              value={b.reviewerRemark || ""}
                              onChange={(e) => {
                                const next = [...permitTypeBlocks];
                                next[idx] = {
                                  ...next[idx],
                                  reviewerRemark: e.target.value,
                                };
                                setPermitTypeBlocks(next);

                                if (next[idx]?.id) {
                                  setEditedBlockIds((prev) =>
                                    prev.includes(next[idx].id)
                                      ? prev
                                      : [...prev, next[idx].id],
                                  );
                                }
                              }}
                              className="w-full border-2 border-blue-500 rounded-md p-2 focus:outline-none focus:border-blue-600"
                              rows={4}
                            />
                          ) : (
                            <div className="text-sm text-gray-600 border rounded-md p-2 bg-white"></div>
                          )
                        ) : (
                          <div className="text-sm text-gray-800 border rounded-md p-2 bg-white">
                            {b.reviewerRemark ? (
                              <pre className="whitespace-pre-wrap m-0">{b.reviewerRemark}</pre>
                            ) : (
                              "-"
                            )}
                          </div>
                        )}
                      </div>
                      <div className="sm:w-64 w-full flex-shrink-0">
                        {!b.approvedById ? (
                          isBlockClearancesApproved(b) &&
                            user &&
                            [
                              ptw_can_approve_hot_work,
                              ptw_can_approve_cold_work,
                              ptw_can_approve_electrical_work,
                              ptw_can_approve_confined_space_entry,
                              ptw_can_approve_work_at_height,
                              ptw_can_approve_excavation_work,
                              ptw_can_approve_chemical_handling,
                            ].some((authority) =>
                              user.authorities === undefined
                                ? false
                                : user.authorities.includes(authority),
                            ) ? (
                            userHasRequiredRole(b) ? (
                              <div className="flex-1">
                                <div className="pd-field-label mb-1">Acceptance</div>
                                <select
                                  value={b.approved == null && b.approved == undefined
                                    ? ""
                                    : b.approved ? "yes" : "no"}
                                  onChange={(e) => {
                                    if (e.target.value === "") {
                                      showError("Select proper Acceptance");
                                    }
                                    const next = [...permitTypeBlocks];
                                    next[idx] = {
                                      ...next[idx],
                                      approved: e.target.value === "yes",
                                    };
                                    setPermitTypeBlocks(next);
                                    if (next[idx] && next[idx].id) {
                                      setEditedBlockIds((prev) =>
                                        prev.includes(next[idx].id)
                                          ? prev
                                          : [...prev, next[idx].id],
                                      );
                                    }
                                  }}
                                  className="border rounded p-1 text-sm bg-white w-full"
                                >
                                  <option value="">Select</option>
                                  <option value="yes">Approve</option>
                                  <option value="no">Reject</option>
                                </select>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-600">
                                You do not have the required role to approve this permit type
                              </div>
                            )
                          ) : null
                        ) : (
                          <div className="flex-1">
                            <div className="pd-field-label mb-1">Acceptance</div>
                            <div className="border rounded-md p-2 bg-white">
                              <span
                                className={`pd-pill ${b.approved == null && b.approved == undefined
                                  ? "pd-pill-neutral"
                                  : b.approved ? "pd-pill-ok" : "pd-pill-crit"
                                  }`}
                              >
                                {b.approved == null && b.approved == undefined
                                  ? "Pending"
                                  : b.approved ? "Approved" : "Rejected"}
                              </span>
                              {b.approvedByName && (
                                <div className="text-xs text-gray-600 mt-1">
                                  By: {b.approvedByName}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

      {/* HOD approval action for issuer department — shown last, right before the review is submitted */}
      {permit &&
        permit.status === "ISSUER_DEPARTMENT_HOD_REVIEW" &&
        user &&
        hasIssuerDepartmentHOD && (
          <div className="mt-3 border rounded p-2 bg-white max-w-screen-lg mx-auto">
            <div className="text-sm font-semibold">Issuer Department HOD Review</div>

            <div className="mt-2">
              <div className={`text-xs font-semibold ${hodEditApproved === false ? 'text-red-600' : 'text-gray-700'}`}>
                Remark {hodEditApproved === false ? '(required when rejecting)' : '(optional)'}
              </div>
              <textarea
                value={hodEditRemark}
                onChange={(e) => {
                  setHodEditRemark(e.target.value);
                  setHodValidationError(false);
                }}
                className="w-full border rounded p-1 mt-1 text-sm"
                rows={3}
              />
              {hodValidationError && (
                <div className="text-xs text-red-600 mt-1">Remark is required when selecting "No".</div>
              )}
            </div>

            <div className="flex items-center gap-2 mt-3">
              <span className="text-sm">Acceptance :</span>
              <select
                value={hodEditApproved === null ? "" : hodEditApproved ? "yes" : "no"}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") setHodEditApproved(null);
                  else setHodEditApproved(v === "yes");
                  setHodValidationError(false);
                }}
                className="border rounded p-1 text-sm bg-white"
              >
                <option value="">Select</option>
                <option value="yes">Approve</option>
                <option value="no">Reject</option>
              </select>
            </div>
          </div>
        )}

      <div id="permit-detail-action-bar" className="max-w-6xl mx-auto px-2 sm:px-6">
        <div className="flex flex-wrap items-center justify-end gap-2 mt-3 px-2 sm:px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {permit && permit?.status === "DRAFT" &&
              user &&
              [ptw_can_create_permit].some((authority) =>
                user.authorities === undefined
                  ? false
                  : user.authorities.includes(authority),
              ) && (
                <button
                  onClick={() => navigate(`/permits/${permit.id}/edit`)}
                  disabled={buttonsDisabled}
                  className="bg-blue-600 text-white px-3 py-2 text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Edit Permit
                </button>
              )}

            {/* DRAFT: Submit or Cancel */}
            {permit && permit?.status === "DRAFT" &&
              user &&
              [ptw_can_create_permit].some((authority) =>
                user.authorities === undefined
                  ? false
                  : user.authorities.includes(authority),
              ) && (
                <button
                  onClick={() => handleAction("submit")}
                  disabled={buttonsDisabled}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit
                </button>
              )}

            {/* UNDER_REVIEW: Approve / Reject */}
            {permit && permit?.status === "SUBMITTED" && (
              <>
                <button
                  onClick={() => handleAction("approve")}
                  disabled={buttonsDisabled}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Approve
                </button>
                <button
                  onClick={() => openRemarkModal("reject")}
                  disabled={buttonsDisabled}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
              </>
            )}

            {/* APPROVED -> Issue (becomes ISSUED) */}
            {permit && permit?.status === "APPROVED" && (
              <button
                onClick={() => handleAction("issue")}
                disabled={buttonsDisabled}
                className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Issue
              </button>
            )}

            {/* WAH scaffold checklist warning (shown on ISSUED permits with WAH type)
            {permit && permit?.status === "ISSUED" &&
              permit?.permitTypeBlocks &&
              permit?.permitTypeBlocks.some(b => b.permitTypeCode === "WAH") &&
              permit?.permitTypeBlocks
                .filter(b => b.permitTypeCode === "WAH")
                .some(b => (b.checklistItems || []).some(ci => ci.mandatory && ci.status !== "DONE")) && (
                <div className="w-full bg-red-50 border border-red-400 rounded-lg p-3 text-sm text-red-800">
                  <strong>⚠ Scaffold Pre-erection Checklist Incomplete</strong>
                  <p className="mt-1">All mandatory scaffold pre-erection checklist items must be marked <strong>DONE</strong> before this Work at Height permit can be activated. Complete them in the permit type block below.</p>
                </div>
              )} */}

            {/* ISSUED -> Activate */}
            {permit && permit?.status === "ISSUED" &&
              user &&
              [ptw_can_create_permit].some((authority) =>
                user.authorities === undefined
                  ? false
                  : user.authorities.includes(authority),
              ) && (
                <button
                  onClick={() => handleAction("activate")}
                  disabled={buttonsDisabled}
                  className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Activate
                </button>
              )}

            {/* SUSPENDED -> Activate (only available after suspend-until time and before schedule end) */}
            {permit && permit?.status === "SUSPENDED" &&
              permit?.suspendUntil &&
              user &&
              [ptw_can_create_permit].some((authority) =>
                user.authorities === undefined
                  ? false
                  : user.authorities.includes(authority),
              ) &&
              (() => {
                try {
                  const until = new Date(permit.suspendUntil);
                  const now = new Date();
                  // Check if suspend-until time has passed
                  if (now >= until) {
                    // Check if suspendUntil is before schedule end time (or extendUntil)
                    const effectiveEnd = permit.extendUntil || permit.scheduleEnd;
                    if (effectiveEnd) {
                      const endDate = new Date(effectiveEnd);
                      if (now < endDate) {
                        // Both conditions met: can activate — open resumption sign-off modal
                        return (
                          <button
                            onClick={() => { setResumptionRemark(""); setShowResumptionModal(true); }}
                            disabled={buttonsDisabled}
                            className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Re-activate
                          </button>
                        );
                      }
                    }
                  }
                } catch (e) {
                  // ignore parse errors and don't render the button
                }
                return null;
              })()}

            {/* ACTIVE -> Suspend / Extend / Close */}
            {permit && permit?.status === "ACTIVE" &&
              user &&
              [ptw_can_create_permit].some((authority) =>
                user.authorities === undefined
                  ? false
                  : user.authorities.includes(authority),
              ) && (
                <>
                  <button
                    onClick={() => openRemarkModal("suspend")}
                    disabled={buttonsDisabled}
                    className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suspend
                  </button>
                  <button
                    onClick={() => openRemarkModal("extend")}
                    disabled={buttonsDisabled}
                    className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Extend
                  </button>
                  <button
                    onClick={() => openRemarkModal("close")}
                    disabled={buttonsDisabled}
                    className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Close
                  </button>
                </>
              )}

            {/* Allow Close when already Suspended or Extended */}
            {permit && (permit?.status === "SUSPENDED" ||
              permit?.status === "EXTENDED") &&
              user &&
              [ptw_can_create_permit].some((authority) =>
                user.authorities === undefined
                  ? false
                  : user.authorities.includes(authority),
              ) && (
                <button
                  onClick={() => openRemarkModal("close")}
                  disabled={buttonsDisabled}
                  className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Close
                </button>
              )}

            {permit && permit?.status === "ISSUER_DEPARTMENT_HOD_REVIEW" &&
              user &&
              hasIssuerDepartmentHOD && (
                <button
                  onClick={async () => {
                    setButtonsDisabledWithLog(true);
                    try {
                      await submitReceiverDepartmentReview();
                    } catch (e) {
                      showError(
                        "Failed to submit HOD decision: " + e.message,
                      );
                    }
                  }}
                  disabled={buttonsDisabled}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit Issuer Department Review
                </button>
              )}

            {permit &&
              (permit?.status === "CLEARANCE_DEPARTMENT_REVIEW" ||
                permit?.status === "EXTEND_UNDER_REVIEW") &&
              !isBlockClearancesApproved() &&
              user &&
              user.authorities &&
              user.authorities.includes(
                ptw_can_review_clearance_department,
              ) &&
              Array.isArray(permit.clearanceDepartments) &&
              permit.clearanceDepartments.some((cc) =>
                userHasDeptAccess(
                  cc.departmentId ||
                  (cc.department && cc.department.id) ||
                  cc.department,
                ),
              ) && (
                <button
                  onClick={async () => {
                    try {
                      const allClearances =
                        permit && Array.isArray(permit.clearanceDepartments)
                          ? permit.clearanceDepartments
                          : [];

                      const isClearanceReviewed = (c) =>
                        !!(
                          c.approvedById ||
                          (c.approvedBy && (c.approvedBy.id || c.approvedById))
                        );

                      // Every department this user can review (and hasn't already been
                      // reviewed by someone else) must have an explicit Approve/Reject
                      // choice before allowing a partial save.
                      const pendingUnselected = (allClearances || []).filter(
                        (c) =>
                          userCanApproveClearance(c) &&
                          !isClearanceReviewed(c) &&
                          (clearanceEdits[c.id] === undefined ||
                            clearanceEdits[c.id] === null),
                      );
                      if (pendingUnselected.length > 0) {
                        showWarning(
                          `Please select Approve or Reject for all clearance department(s): ${pendingUnselected
                            .map((c) => c.departmentName || c.departmentId)
                            .join(", ")}.`,
                        );
                        return;
                      }

                      // Only submit departments that are actually still awaiting review by
                      // this user — never resend already-reviewed departments, or the
                      // backend will overwrite their recorded reviewer with the current user.
                      const toSave = (allClearances || [])
                        .filter(
                          (c) =>
                            userCanApproveClearance(c) && !isClearanceReviewed(c),
                        )
                        .map((c) => {
                          const cid = c.id;
                          const approved =
                            clearanceEdits[cid] !== undefined
                              ? !!clearanceEdits[cid]
                              : !!c.approved;
                          const remark =
                            clearanceRemarks[cid] !== undefined
                              ? clearanceRemarks[cid]
                                ? String(clearanceRemarks[cid])
                                : null
                              : c.remark
                                ? String(c.remark)
                                : null;
                          return {
                            id: Number(cid),
                            approved,
                            remark,
                          };
                        })
                        .filter((x) => x && x.id);

                      if (!toSave || toSave.length === 0) {
                        showInfo(
                          "No clearance changes to save (no accessible departments)",
                        );
                        return;
                      }

                      const missing = (toSave || []).some(
                        (c) =>
                          c &&
                          c.approved === false &&
                          (!c.remark || c.remark.trim() === ""),
                      );
                      if (missing) {
                        const errMap = {};
                        (toSave || []).forEach((c) => {
                          if (
                            c &&
                            c.approved === false &&
                            (!c.remark || c.remark.trim() === "")
                          ) {
                            errMap[c.id] = true;
                          }
                        });
                        setClearanceValidationErrors(errMap);
                        const firstId = Object.keys(errMap)[0];
                        setTimeout(() => {
                          try {
                            const el = document.getElementById(
                              `clearance-remark-${firstId}`,
                            );
                            if (el) el.focus();
                          } catch (e) { }
                        }, 50);
                        showWarning(
                          "Please provide remark(s) for clearance(s) with 'No' decision.",
                        );
                        return;
                      }
                      setButtonsDisabledWithLog(true);
                      const url = `${restApiUrl}/api/permits/${id}/save-review`;
                      const respC = await fetch(url, {
                        method: "POST",
                        mode: "cors",
                        headers: headers(),
                        body: JSON.stringify({
                          clearanceDepartments: toSave,
                          remark: null,
                        }),
                      });
                      const jsonC = await respC.json();
                      if (!jsonC.success) {
                        throw new Error(
                          jsonC.error ||
                          "Failed to save clearance review",
                        );
                      }

                      setClearanceServerApproved((prev) => {
                        const next = { ...(prev || {}) };
                        (toSave || []).forEach((c) => {
                          if (c && c.id) next[c.id] = !!c.approved;
                        });
                        return next;
                      });
                      setClearanceEdits((prev) => {
                        const next = { ...(prev || {}) };
                        (toSave || []).forEach((c) => {
                          if (c && c.id) next[c.id] = !!c.approved;
                        });
                        return next;
                      });
                      if (
                        (permit.status || "")
                          .toUpperCase()
                          .includes("EXTEND") ||
                        permit.status === "EXTENDED"
                      ) {
                        // handleAction("activate");
                        window.location.reload();
                      } else {
                        showSuccess("Clearance review saved successfully");
                        window.location.reload();
                      }
                    } catch (err) {
                      showError(
                        "Failed to save clearance review: " + err.message,
                      );
                    } finally {
                      setButtonsDisabledWithLog(false);
                    }
                  }}
                  disabled={buttonsDisabled}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit Clearance Review
                </button>
              )}
            {permit && permit?.status === "COMPETENT_PERSON_APPROVAL" ? (
              <>
                {user &&
                  [ptw_can_review_competent_person].some((authority) =>
                    user.authorities === undefined
                      ? false
                      : user.authorities.includes(authority),
                  ) && (
                    <button
                      disabled={buttonsDisabled}
                      onClick={async () => {
                        setButtonsDisabledWithLog(true);
                        try {
                          // Build payload containing competent-person fields for confined permit types
                          const payload = {
                            permitTypeBlocks: (permitTypeBlocks || [])
                              .filter(
                                (b) =>
                                  b.id &&
                                  b.permitTypeName &&
                                  b.permitTypeName
                                    .toLowerCase()
                                    .includes("confined"),
                              )
                              .map((b) => ({
                                id: b.id,
                                competentPersonName:
                                  b.competentPersonName || null,
                                competentPersonApproved:
                                  b.competentPersonApproved !== undefined
                                    ? !!b.competentPersonApproved
                                    : null,
                              })),
                            remark: null,
                          };

                          const resp = await fetch(
                            `${restApiUrl}/api/permits/${id}/competent-approve?actorId=1`,
                            {
                              method: "POST",
                              mode: "cors",
                              headers: headers(),
                              body: JSON.stringify(payload),
                            },
                          );
                          const json = await resp.json();
                          if (!json.success)
                            throw new Error(
                              json.error ||
                              "Failed to submit competent approval",
                            );
                          showSuccess("Competent approval submitted successfully");
                          window.location.reload();
                        } catch (err) {
                          showError("Failed: " + err.message);
                        } finally {
                          setButtonsDisabledWithLog(false);
                        }
                      }}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded shadow disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Submit Competent Approval
                    </button>
                  )}
              </>
            ) : (
              permit?.status === "PERMIT_TYPE_REVIEW" && (
                <>
                  {user &&
                    [
                      ptw_can_approve_hot_work,
                      ptw_can_approve_cold_work,
                      ptw_can_approve_electrical_work,
                      ptw_can_approve_confined_space_entry,
                      ptw_can_approve_work_at_height,
                      ptw_can_approve_excavation_work,
                      ptw_can_approve_chemical_handling,
                    ].some((authority) =>
                      user.authorities === undefined
                        ? false
                        : user.authorities.includes(authority),
                    ) && (
                      <button
                        disabled={buttonsDisabled}
                        onClick={async () => {
                          try {
                            // Every permit-type block this user is permitted (and eligible) to
                            // review right now must have an explicit Approve/Reject choice —
                            // partial saves that skip some of them are not allowed.
                            const reviewablePendingBlocks = (
                              permitTypeBlocks || []
                            ).filter(
                              (pb) =>
                                pb.id &&
                                isBlockClearancesApproved(pb) &&
                                userHasRequiredRole(pb) &&
                                (pb.approved === null || pb.approved === undefined),
                            );
                            const missingBlocks = reviewablePendingBlocks.filter(
                              (pb) => !editedBlockIds.includes(pb.id),
                            );
                            if (missingBlocks.length > 0) {
                              showWarning(
                                `Please select Approve or Reject for all permit type(s) you can review: ${missingBlocks
                                  .map(
                                    (pb) =>
                                      pb.permitTypeName ||
                                      (pb.permitType && pb.permitType.name) ||
                                      "Permit Type",
                                  )
                                  .join(", ")}.`,
                              );
                              return;
                            }

                            // Compute edited permit-type blocks and clearance approvals
                            const changed = (permitTypeBlocks || []).filter(
                              (pb) => pb.id && editedBlockIds.includes(pb.id),
                            );
                            const changedClearances = Object.keys(
                              clearanceEdits,
                            )
                              .map((cid) => ({
                                id: Number(cid),
                                approved: !!clearanceEdits[cid],
                                remark: clearanceRemarks[cid] || null,
                              }))
                              .filter((c) => c.id);
                            if (
                              (!changed || changed.length === 0) &&
                              (!changedClearances ||
                                changedClearances.length === 0)
                            ) {
                              showInfo("No changes to save");
                              return;
                            }
                            setButtonsDisabledWithLog(true);
                            const url = `${restApiUrl}/api/permits/${id}/save-review?actorId=1`;

                            // First: save clearance approvals (if any)
                            if (
                              changedClearances &&
                              changedClearances.length > 0
                            ) {
                              // validate remarks for any 'No' clearance
                              const missing = (changedClearances || []).some(
                                (c) =>
                                  c &&
                                  c.approved === false &&
                                  (!c.remark || c.remark.trim() === ""),
                              );
                              if (missing) {
                                const errMap = {};
                                (changedClearances || []).forEach((c) => {
                                  if (
                                    c &&
                                    c.approved === false &&
                                    (!c.remark || c.remark.trim() === "")
                                  ) {
                                    errMap[c.id] = true;
                                  }
                                });
                                setClearanceValidationErrors(errMap);
                                const firstId = Object.keys(errMap)[0];
                                setTimeout(() => {
                                  try {
                                    const el = document.getElementById(
                                      `clearance-remark-${firstId}`,
                                    );
                                    if (el) el.focus();
                                  } catch (e) { }
                                }, 50);
                                showWarning(
                                  "Please provide remark(s) for clearance(s) with 'No' decision.",
                                );
                                return;
                              }
                              const bodyC = {
                                clearanceDepartments: changedClearances,
                                remark: null,
                              };
                              const respC = await fetch(url, {
                                method: "POST",
                                mode: "cors",
                                headers: headers(),
                                body: JSON.stringify(bodyC),
                              });
                              const jsonC = await respC.json();
                              if (!jsonC.success)
                                throw new Error(
                                  jsonC.error ||
                                  "Failed to save clearance review",
                                );
                              // update server-approved map to reflect saved values (backend confirmed)
                              setClearanceServerApproved((prev) => {
                                const next = { ...(prev || {}) };
                                (changedClearances || []).forEach((c) => {
                                  if (c && c.id) next[c.id] = !!c.approved;
                                });
                                return next;
                              });
                              // optionally sync UI edits to the saved values
                              setClearanceEdits((prev) => {
                                const next = { ...(prev || {}) };
                                (changedClearances || []).forEach((c) => {
                                  if (c && c.id) next[c.id] = !!c.approved;
                                });
                                return next;
                              });
                            }

                            // Then: save permit-type blocks (only those changed AND whose clearances are approved)
                            const payload = (changed || []).map((pb) => ({
                              id: pb.id,
                              reviewerRemark: pb.reviewerRemark || null,
                              approved:
                                pb.approved !== undefined
                                  ? !!pb.approved
                                  : null,
                              competentPersonName:
                                pb.competentPersonName || null,
                              competentPersonApproved:
                                pb.competentPersonApproved !== undefined
                                  ? !!pb.competentPersonApproved
                                  : null,
                            }));

                            if (payload && payload.length > 0) {
                              // validate that each changed block's clearances are approved
                              const invalid = (changed || []).some(
                                (pb) => !isBlockClearancesApproved(pb),
                              );
                              if (invalid) {
                                showWarning(
                                  "Cannot save permit-type approvals while its clearance(s) are not approved.",
                                );
                                return;
                              }
                              const bodyP = {
                                permitTypeBlocks: payload,
                                remark: null,
                              };
                              const respP = await fetch(url, {
                                method: "POST",
                                mode: "cors",
                                headers: headers(),
                                body: JSON.stringify(bodyP),
                              });
                              const jsonP = await respP.json();
                              if (!jsonP.success)
                                throw new Error(
                                  jsonP.error ||
                                  "Failed to save permit-type review",
                                );
                            }

                            setEditedBlockIds([]);
                            showSuccess("Review saved successfully");
                            window.location.reload();
                          } catch (err) {
                            showError("Failed to save review: " + err.message);
                          } finally {
                            setButtonsDisabledWithLog(false);
                          }
                        }}
                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded shadow disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Submit Review
                      </button>
                    )}
                  {user &&
                    [
                      ptw_can_review_competent_person,
                      ptw_can_approve_hot_work,
                      ptw_can_approve_cold_work,
                      ptw_can_approve_electrical_work,
                      ptw_can_approve_confined_space_entry,
                      ptw_can_approve_work_at_height,
                      ptw_can_approve_excavation_work,
                      ptw_can_approve_chemical_handling,
                    ].some((authority) =>
                      user.authorities === undefined
                        ? false
                        : user.authorities.includes(authority),
                    ) && (
                      <button
                        onClick={() => {
                          // Prefill remark modal with the current returnComment and open it
                          setRemarkAction("return");
                          setRemarkText(returnComment || "");
                          setRemarkDate("");
                          setShowRemarkModal(true);
                        }}
                        disabled={buttonsDisabled}
                        className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded shadow disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Return to Draft
                      </button>
                    )}
                </>
              )
            )}

            {/* Cancel is available at any step except closed/cancelled/rejected */}
            {permit && permit?.status !== "CLOSED" &&
              permit?.status !== "CANCELLED" &&
              permit?.status !== "REJECTED" &&
              user &&
              [
                ptw_can_create_permit,
                ptw_can_review_competent_person,
                ptw_can_approve_hot_work,
                ptw_can_approve_cold_work,
                ptw_can_approve_electrical_work,
                ptw_can_approve_confined_space_entry,
                ptw_can_approve_work_at_height,
                ptw_can_approve_excavation_work,
                ptw_can_approve_chemical_handling,
              ].some((authority) =>
                user.authorities === undefined
                  ? false
                  : user.authorities.includes(authority),
              ) && (
                <button
                  onClick={async () => {
                    const confirmed = await showConfirm(
                      "Are you sure you want to cancel this permit?",
                      {
                        title: "Cancel Permit",
                        severity: "error",
                      },
                    );
                    if (!confirmed) return;
                    openRemarkModal("cancel");
                  }}
                  disabled={buttonsDisabled}
                  className=" text-sm font-medium bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel Permit
                </button>
              )}
          </div>
        </div>
      </div>
    </div>

  );
};

export default PermitDetail;
